var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Cartesian2, Cartesian3, Color, ScreenSpaceEventType, createGuid, } from "cesium";
import { exportToSIMAFile } from "./SIMA/sima-exporter";
import { SIMAAdapter } from "./adapters/sima-adapter";
import { CesiumPointSelectorFactory, CesiumPointSelectorType, } from "./cesium-utils/point-selector/point-selector";
import { CommandManager } from "./commands";
import { HeightMapUtils } from "./core/utils";
import { CrossSectionManager } from "./cross-sections/cross-section-manager";
import { LineWorkCrossSectionHandler } from "./cross-sections/linework-cross-section-handler";
import { generateCenterlineCrossSectionPoints } from "./cross-sections/centerline-cross-section-points";
import { generateTraditionalCrossSectionPoints } from "./cross-sections/traditional-cross-section-points";
import { ArbitraryLineHandler } from "./handler/arbitrary-line/arbitraryLine-handler";
import { Drawing2D2PointAlignmentHandler } from "./handler/drawing-2d-image/drawing-2d-2Point-Alignment-handler";
import { Drawing2DConstructionHandler } from "./handler/drawing-2d-image/drawing-2d-construction-handler";
import { Drawing2DImageEditHandler } from "./handler/drawing-2d-image/drawing-2d-edit-handler";
import { FlatGroundHandler } from "./handler/flatground/flatground-handler";
import { GenericHandler } from "./handler/generic-handler";
import { RoadHandler } from "./handler/road/road-handler";
import { RoadAiPointPickHandler } from "./handler/road-ai/road-ai-point-pick-handler";
import { SolidMeshHandler } from "./handler/solid-mesh/solid-mesh-handler";
import { FaceSelectionHandler } from "./handler/spreading-compaction/face-selection-handler";
import { TrenchHandler } from "./handler/trench/trench-handler";
import { MeasurementManager } from "./measurements/earthbrain-measurement";
import { MeasurementOperation, } from "./measurements/measurement-interface";
import { PinManager } from "./pins/PinManager";
import { LineWorkProfileHandler } from "./profiles/linework-profile-handler";
import { CesiumService } from "./services/cesium-services";
import { ViewerSettings, } from "./settings/viewer-settings";
import { ArbitraryLine } from "./site/arbitrary-line/earthbrain-arbitrary-line";
import { PointType, } from "./site/arbitrary-line/types";
import { DXFWGS84 } from "./site/dxf/dxf-wgs84";
import { EarthbrainDXF } from "./site/dxf/earthbrain-dxf";
import { Site } from "./site/site";
import { HorizontalBasePlane } from "./site/spreading-compaction/horizontal-base-plane";
import { SpreadingCompaction } from "./site/spreading-compaction/spreading-compaction";
import { DesignTileSet } from "./site/tileset/design";
import { ConstructionState, } from "./site/trench/earthbrain-trench";
import { ERubbingDestination } from "./site/trench/types";
import { AssetType, ViewType, } from "./types";
import { Activity, ActivityLogger } from "./utilities/activity-logger";
import { EarthBrainUtils } from "./utilities/earthbrainutility";
import { FunctionLogger } from "./utilities/function-logger";
import { buildHorizontalPlaneGeometry } from "./utilities/horizontal-base-plane";
import { describeBoundary, extendOpenBoundaryToTerrain, findNonSimpleBoundaryVertices, stitchTJunctions, weldVertices, } from "./utilities/mesh-terrain-extension";
import { getRaycastableFromRubbingDestination } from "./utilities/structure-utilities";
import { AltitudeView } from "./viewers/view-2d-altitude";
import { LineProfileView } from "./viewers/view-2d-line-profile";
import { LineSectionView } from "./viewers/view-2d-line-section";
import { MeasurementView } from "./viewers/view-2d-measurement";
import { SectionView } from "./viewers/view-2d-section";
import { CesiumView, } from "./viewers/view-3d-cesium";
import { SIMAManager } from "./SIMA/sima-manager";
import { alignmentToSIMA } from "./SIMA/alignmentToSIMA";
/**
 * This decorator should be kept at the last if used with other decorators
 * @returns
 */
function needsRequestRender() {
    const retVal = (target, key, descriptor) => {
        const original = descriptor.value;
        descriptor.value = function (...args) {
            const returnVal = original.apply(this, args);
            if (returnVal instanceof Promise) {
                returnVal.then(() => {
                    this._cesiumView.viewer.scene.requestRender();
                }, 
                // 呼び出し元は returnVal で reject を受け取る。この派生 Promise を未処理 reject にしないためだけに受ける
                () => undefined);
            }
            else {
                this._cesiumView.viewer.scene.requestRender();
            }
            return returnVal;
        };
        return descriptor;
    };
    return retVal;
}
export class SCDesign {
    _cesiumView;
    _altitudeView;
    _sectionView;
    _measurementView;
    _lineSectionView;
    _lineProfileView;
    _settings;
    _pinManager;
    _measurementManager;
    _crossSectionManager;
    _simaManager;
    _lineWorkCrossSectionHandler;
    _commandManager;
    _site;
    _trenchHandler;
    _roadHandler;
    _roadAiPointPickHandler;
    _flatGroundHandler;
    _arbitraryHandler;
    _lineWorkProfileHandler;
    _drawing2dImageEditHandler;
    _drawing2dImage2PointAlignmentHandler;
    _drawing2dImageConstructionHandler;
    _genericHandler;
    _solidMeshHandler;
    _faceSelectionHandler;
    /** 面選択中の対象ジオメトリ (Local)。水平基準面の XY 範囲に使う */
    _faceSelectionLocalGeometry = null;
    _faceSelectionBaseMode = "faces";
    _horizontalBasePlane = null;
    _cesiumPointSelectorFactory;
    _cesiumRenderer;
    _update2DViews(entityId, fit, type) {
        let entity;
        let distanceFromStart;
        if (type === AssetType.Road_Data) {
            entity = this._site.getRoad(entityId);
            distanceFromStart =
                this._roadHandler.editHandler.sectionPlaneDistanceFromStart;
        }
        if (type === AssetType.Trench_Data) {
            entity = this._site.getTrench(entityId);
            distanceFromStart =
                this._trenchHandler.editHandler.sectionPlaneDistanceFromStart;
        }
        if (!entity)
            return;
        const data = entity.get2DViewerData({ distanceFromStart });
        this._altitudeView.renderData(entityId, data.altitudeViewData, fit, this._settings.presentation, type);
        this._sectionView.renderData(entityId, data.sectionViewData, fit, this._settings.presentation, type);
    }
    getCurrentEntityDataInAltitudeView() {
        const { currentEntityID, currentEntityType } = this._altitudeView;
        return {
            currentEntityID,
            currentEntityType,
        };
    }
    _update2DViewsIfEntitySelected() {
        const { currentEntityID, currentEntityType } = this.getCurrentEntityDataInAltitudeView();
        if (currentEntityID && currentEntityType) {
            this._update2DViews(currentEntityID, false, currentEntityType);
        }
    }
    constructor(args, settings) {
        ActivityLogger.log(Activity.APP_INITIALIZED);
        this._settings =
            settings || new ViewerSettings(args.createDistanceUnitPort);
        this._site = Site.getInstance(args.localTransformCallback, args.worldTransformCallback);
        this._commandManager = new CommandManager();
        this._cesiumView = new CesiumView(args.cesiumContainer, args.token, args.arcgisToken, {
            enableSceneModePicker: args.enableSceneModePicker,
            enableDesignDataOutline: args.enableDesignDataOutline,
        }, this._site, this._commandManager);
        CesiumService.getInstance(this._cesiumView.viewer);
        this._pinManager = new PinManager(this._cesiumView.viewer);
        this._measurementManager = new MeasurementManager(this._site, this._cesiumView.viewer, this._settings.distanceUnitPort);
        this._simaManager = new SIMAManager(this._cesiumView.viewer);
        this._altitudeView = new AltitudeView(this._settings.presentation);
        this._sectionView = new SectionView(this._settings.presentation);
        this._measurementView = new MeasurementView(this._settings.presentation);
        this._lineSectionView = new LineSectionView(this._settings.presentation);
        this._lineProfileView = new LineProfileView(this._settings.presentation);
        this._crossSectionManager = new CrossSectionManager();
        this._cesiumPointSelectorFactory = new CesiumPointSelectorFactory({
            site: this._site,
            viewer: this._cesiumView.viewer,
            handler: this._cesiumView.handler,
        });
        const handlerArgs = {
            site: this._site,
            viewer: this._cesiumView.viewer,
            cesiumPicker: this._cesiumView.picker,
            commandManger: this._commandManager,
            handler: this._cesiumView.handler,
            preview: this._cesiumView.preview,
            lineWorkCrossSectionHandler: this._lineWorkCrossSectionHandler,
            cesiumPointSelectorFactory: this._cesiumPointSelectorFactory,
        };
        this._trenchHandler = new TrenchHandler(handlerArgs, this._cesiumView);
        this._roadHandler = new RoadHandler(handlerArgs, this._cesiumView);
        this._roadAiPointPickHandler = new RoadAiPointPickHandler(handlerArgs);
        this._flatGroundHandler = new FlatGroundHandler(handlerArgs, this._cesiumView);
        this._arbitraryHandler = new ArbitraryLineHandler(handlerArgs, this._cesiumView, this._lineProfileView);
        this._lineWorkCrossSectionHandler = new LineWorkCrossSectionHandler(args.lineWorkCrossSectionClickCallback, handlerArgs, this._crossSectionManager, this._lineSectionView);
        this._lineWorkProfileHandler = new LineWorkProfileHandler(handlerArgs, this._lineProfileView);
        this._solidMeshHandler = new SolidMeshHandler(handlerArgs, this._cesiumView);
        this._faceSelectionHandler = new FaceSelectionHandler(this._cesiumView.viewer);
        this._genericHandler = new GenericHandler(handlerArgs, this._trenchHandler, this._roadHandler, this._flatGroundHandler, this._arbitraryHandler, this._lineWorkCrossSectionHandler, this._lineWorkProfileHandler);
        this._drawing2dImageConstructionHandler = new Drawing2DConstructionHandler(handlerArgs, this._cesiumView);
        this._drawing2dImageEditHandler = new Drawing2DImageEditHandler(handlerArgs, this._cesiumView, this._site);
        this._drawing2dImage2PointAlignmentHandler =
            new Drawing2D2PointAlignmentHandler(handlerArgs, this._cesiumView, this._site);
        this._cesiumView.handler.setInputAction(this._genericHandler.leftDownHandler.bind(this._genericHandler), ScreenSpaceEventType.LEFT_DOWN);
    }
    // 2D Viewer
    clear2DViews() {
        this._altitudeView.clearView();
        this._sectionView.clearView();
        this._measurementView.clearView();
    }
    getCanvasForView(viewName) {
        let canvas;
        switch (viewName) {
            case ViewType.SECTION_VIEW:
                canvas = this._sectionView.getCanvas();
                break;
            case ViewType.ALTITUDE_VIEW:
                canvas = this._altitudeView.getCanvas();
                break;
            case ViewType.MEASUREMENT_VIEW:
                canvas = this._measurementView.getCanvas();
                break;
            case ViewType.LINE_SECTION_VIEW:
                canvas = this._lineSectionView.getCanvas();
                break;
            case ViewType.LINE_PROFILE_VIEW:
                canvas = this._lineProfileView.getCanvas();
                break;
            case ViewType.CESIUM_VIEW:
            default:
                canvas = undefined;
        }
        return canvas;
    }
    resize2dView(size, viewName) {
        switch (viewName) {
            case ViewType.SECTION_VIEW:
                this._sectionView.resizeView(size);
                break;
            case ViewType.ALTITUDE_VIEW:
                this._altitudeView.resizeView(size);
                break;
            case ViewType.MEASUREMENT_VIEW:
                this._measurementView.resizeView(size);
                break;
            case ViewType.LINE_SECTION_VIEW:
                this._lineSectionView.resizeView(size);
                break;
            case ViewType.LINE_PROFILE_VIEW:
                this._lineProfileView.resizeView(size);
        }
    }
    setSectionViewLabels(labels) {
        this._sectionView.setLabels(labels);
    }
    // For Cesium View returns the data for 3D control points
    // For Altitude View returns the data for slope breaks
    pickPathandPoint(x, y, viewerName) {
        if (viewerName === "cesiumView") {
            return this._roadHandler.editHandler.pickPathandPoint(x, y);
        }
        if (viewerName === "altitudeView") {
            return this._altitudeView.pickPathAndPoint(x, y);
        }
    }
    pickTrenchEntities(x, y, viewerName) {
        if (viewerName === "cesiumView") {
            return this._trenchHandler.editHandler.pickPathAndPoint(x, y);
        }
        // if (viewerName === "altitudeView") {
        // }
    }
    // 3d Viewer
    /**
     * The function will check whether the mouse is dragged after right clickng & before releasing it
     * @returns returns true or false which helps to decide whether context menu should duplay or not in creation mode
     */
    // Todo
    isMouseDraggedAfterRightClick() {
        return this._cesiumView.isMouseDraggedAfterRightClick();
    }
    // Features
    async undo() {
        await this._commandManager.undo(this._cesiumView);
    }
    async redo() {
        await this._commandManager.redo(this._cesiumView);
    }
    /**
     * API used to zoom in to the road attached with given road ID
     * @param roadID
     */
    fitToRoad(roadID) {
        const road = this._site.getRoad(roadID);
        if (!road) {
            return;
        }
        this._roadHandler.fitToRoad(road);
        if (road.isCenterLineVisible) {
            this._lineWorkProfileHandler.renderProfileData(road.alignment);
        }
        this._update2DViews(road.id, true, AssetType.Road_Data);
    }
    /**
     * Changes the road in 2D viewer for both cross section and logitudinal
     * @param roadID roadID of the road which needs to be displayed in 2D viewer
     */
    //  public changeRoadIn2DViewer(roadID: string) {
    //     const road = this._site.getRoad(roadID);
    //     if (road && this._roadHandler.editHandler.roadUnderEdit && road.id === this._roadHandler.editHandler.roadUnderEdit.id) {
    //       this._roadHandler.editHandler.updateSectionPlaneWith2DViews(0);
    //     }
    //   }
    /**
     * Updates the visibility of the road related to provided roadID
     * @param roadID
     * @param show
     */
    toggleRoadVisibility(roadID, show) {
        const road = this._site.getRoad(roadID);
        if (!road) {
            return;
        }
        this._roadHandler.toggleRoadVisibility(road, show);
        if (!show) {
            this._lineWorkProfileHandler.dispose(road.alignment.id);
        }
        else if (road.isCenterLineVisible) {
            this._lineWorkProfileHandler.renderProfileData(road.alignment);
        }
    }
    setRoadAlignmentVisibility(roadID, visible) {
        const road = this._site.getRoad(roadID);
        if (!road) {
            return;
        }
        road.setAlignmentVisibility(visible);
    }
    setRoadIpLineVisibility(roadID, visible) {
        const road = this._site.getRoad(roadID);
        if (!road) {
            return;
        }
        road.setIpLineVisibility(visible);
    }
    setRoadCenterLineVisibility(roadID, visible) {
        const road = this._site.getRoad(roadID);
        if (!road) {
            return;
        }
        road.setCenterLineVisibility(visible);
        if (visible) {
            this._lineWorkProfileHandler.renderProfileData(road.alignment);
        }
        else {
            this._lineWorkProfileHandler.dispose(road.alignment.id);
        }
    }
    setRoadSurfaceVisibility(roadID, visible) {
        const road = this._site.getRoad(roadID);
        if (!road) {
            return;
        }
        road.setSurfaceVisibility(visible);
    }
    setRoadEmbankmentVisibility(roadID, visible) {
        const road = this._site.getRoad(roadID);
        if (!road) {
            return;
        }
        road.setEmbankmentVisibility(visible);
    }
    setRoadFillEmbankmentVisibility(roadID, visible) {
        const road = this._site.getRoad(roadID);
        if (!road) {
            return;
        }
        road.setFillEmbankmentVisibility(visible);
    }
    setRoadCutEmbankmentVisibility(roadID, visible) {
        const road = this._site.getRoad(roadID);
        if (!road) {
            return;
        }
        road.setCutEmbankmentVisibility(visible);
    }
    /**
     * Re-generates road geometry from current in-memory slope break data and shows the road.
     * Used when re-showing a road that was hidden during an edit session.
     */
    updateLongitudeRenderingType(slopeTextRenderType) {
        this._settings.longtiudeSlopeTextRenderType = slopeTextRenderType;
        this._altitudeView.updateSlopeRenderingType(slopeTextRenderType);
    }
    setAltitudeTooltipCallback(callback) {
        this._altitudeView.setTooltipCallback(callback);
    }
    /**
     * Rerender the altitude view considering the new unit type.
     * @param unitType
     */
    updateViewerUnit(unitType) {
        this._settings.viewerUnitType = unitType;
        const presentation = this._settings.presentation;
        this._altitudeView.updatePresentationAndReRender(presentation);
        this._sectionView.updatePresentation(presentation);
        this._lineProfileView.updatePresentation(presentation);
        this._lineSectionView.updatePresentation(presentation);
        this._measurementView.updatePresentation(presentation);
        this.requestRender();
        void this._measurementManager
            .updateUnit(this._settings.distanceUnitPort)
            .catch((error) => {
            console.error("Failed to update measurement unit", error);
        })
            .finally(() => {
            this.requestRender();
        });
    }
    getViewerUnit() {
        return this._settings.viewerUnitType;
    }
    /**
     * Returns the object containing minimum elevation and maximum elevation of the road
     * @param roadID
     * @returns
     */
    getRoadHeightInfoOfSection(roadID, startIndex, endIndex) {
        const road = this._site.getRoad(roadID);
        if (!road) {
            return undefined;
        }
        const heightInfo = road.getHeightInfo();
        return heightInfo;
    }
    /**
     * Returns the object containing minimum slope value and maximum slope value of the road
     * @param roadID
     * @returns
     */
    getRoadSlopeInfoOfSection(roadID, startIndex, endIndex) {
        const road = this._site.getRoad(roadID);
        return road ? road.getSlopeInfo() : undefined;
    }
    updateTilesetTransparency(id, type, value) {
        const tileSet = this._site.getTerrain(id, type);
        if (tileSet) {
            tileSet.updateTransparency(value);
        }
        else {
            console.warn(`${type} tileset with id ${id} not found`);
        }
    }
    updateTilesetColor(id, color, type) {
        const tileSet = this._site.getTerrain(id, type);
        if (tileSet) {
            tileSet.updateColor(color);
        }
        else {
            console.warn(`${type} tileset with id ${id} not found`);
        }
    }
    updateTilesetVisibility(id, type, status) {
        const tileSet = this._site.getTerrain(id, type);
        if (tileSet) {
            tileSet.updateVisibility(status);
            if (tileSet.assetType === AssetType.Point_Cloud) {
                ActivityLogger.log(Activity.POINT_CLOUD_VISIBILITY_UPDATE);
            }
            if (tileSet.assetType === AssetType.Design_Data) {
                ActivityLogger.log(Activity.DESIGNDATA_VISIBILITY_UPDATE);
            }
            if (this.checkRoadUnderCreation() ||
                this.checkFlatGroundUnderCreation()) {
                this._cesiumView.preview.setSphereVisibility(status);
            }
        }
        else {
            console.warn(`${type} tileset with id ${id} not found`);
        }
        const currentEntity2DProfilesUnderView = this._altitudeView.currentEntityID;
        const currentEntityType2DProfilesUnderView = this._altitudeView.currentEntityType;
        if (currentEntity2DProfilesUnderView) {
            this._update2DViews(currentEntity2DProfilesUnderView, false, currentEntityType2DProfilesUnderView);
        }
    }
    updateDrawing2dImageVisibility(id, status) {
        const drawing2dImage = this._site.getDrawing2dImage(id);
        if (drawing2dImage) {
            if (this._drawing2dImageEditHandler.imageUnderEdit?.id === id) {
                drawing2dImage.updateVisibility(false);
                this._drawing2dImageEditHandler.setVisibility(status);
            }
            else {
                drawing2dImage.updateVisibility(status);
            }
            ActivityLogger.log(Activity.REFERENCE_TOPOLOGY_VISIBILITY_UPDATE);
            const currentEntity2DProfilesUnderView = this._altitudeView.currentEntityID;
            const currentEntityType2DProfilesUnderView = this._altitudeView.currentEntityType;
            if (currentEntity2DProfilesUnderView) {
                this._update2DViews(currentEntity2DProfilesUnderView, false, currentEntityType2DProfilesUnderView);
            }
        }
        else {
            console.warn(`Drawing2dImage with id ${id} not found`);
        }
    }
    zoomToTileset(id, type) {
        const tileSet = this._site.getTerrain(id, type);
        if (tileSet) {
            tileSet.zoomTo();
        }
        else {
            console.warn(`${type} tileset with id ${id} not found`);
        }
    }
    zoomToDrawing2dImage(id) {
        const drawing2dImage = this._site.getDrawing2dImage(id);
        if (drawing2dImage) {
            drawing2dImage.zoomTo();
        }
        else {
            console.warn(`Drawing2dImage with id ${id} not found`);
        }
    }
    updateDrawing2dImageTransparency(id, alpha) {
        const drawing2dImage = this._site.getDrawing2dImage(id);
        if (drawing2dImage) {
            if (this._drawing2dImageEditHandler.imageUnderEdit?.id === id) {
                this._drawing2dImageEditHandler.updateTransparency(alpha);
            }
            else {
                drawing2dImage.updateTransparency(alpha);
            }
        }
        else {
            console.warn(`Drawing2dImage with id ${id} not found`);
        }
    }
    removeDrawing2dImage(id) {
        this._site.deleteReferenceTopology(id);
        ActivityLogger.log(Activity.REFERENCE_TOPOLOGY_EDITED);
        const currentEntity2DProfilesUnderView = this._altitudeView.currentEntityID;
        const currentEntityType2DProfilesUnderView = this._altitudeView.currentEntityType;
        if (currentEntity2DProfilesUnderView) {
            this._update2DViews(currentEntity2DProfilesUnderView, false, currentEntityType2DProfilesUnderView);
        }
    }
    async setElevationOfDrawing2dImage(id, elevation) {
        const drawing2dImage = this._site.getDrawing2dImage(id);
        if (drawing2dImage &&
            this._drawing2dImageEditHandler.imageUnderEdit?.id === id) {
            await this._drawing2dImageEditHandler.updateElevation(elevation);
        }
        else {
            console.warn(`Drawing2dImage with id ${id} not found`);
        }
    }
    getElevationOfDrawing2dImage(id) {
        const drawing2dImage = this._site.getDrawing2dImage(id);
        if (drawing2dImage && !drawing2dImage.isDraped()) {
            return drawing2dImage.getElevation();
        }
        else {
            console.warn(`Drawing2dImage with id ${id} not found`);
        }
    }
    getDrawing2DImageMeshGeometry(id) {
        const drawing2dImage = this._site.getDrawing2dImage(id);
        if (drawing2dImage) {
            return drawing2dImage.getMeshGeometry();
        }
        else {
            console.warn(`Drawing2dImage with id ${id} not found`);
        }
    }
    async startEditDrawing2dImage(id, callback) {
        const image = this._site.getDrawing2dImage(id);
        if (image) {
            await this._drawing2dImageEditHandler.start(image, callback);
        }
        else {
            console.warn(`Drawing2dImage with id ${id} not found`);
        }
    }
    async endEditDrawing2dImage(args) {
        await this._drawing2dImageEditHandler.end(args);
        const currentEntity2DProfilesUnderView = this._altitudeView.currentEntityID;
        const currentEntityType2DProfilesUnderView = this._altitudeView.currentEntityType;
        if (currentEntity2DProfilesUnderView) {
            this._update2DViews(currentEntity2DProfilesUnderView, false, currentEntityType2DProfilesUnderView);
        }
    }
    async cancelEditDrawing2dImage() {
        await this._drawing2dImageEditHandler.cancel();
    }
    async start2PointsAlignment(id, callback) {
        const image = this._site.getDrawing2dImage(id);
        if (image) {
            await this._drawing2dImage2PointAlignmentHandler.start(image, callback);
        }
        else {
            console.warn(`Drawing2dImage with id ${id} not found`);
        }
    }
    async end2PointsAlignment(args) {
        await this._drawing2dImage2PointAlignmentHandler.end(args);
        const currentEntity2DProfilesUnderView = this._altitudeView.currentEntityID;
        const currentEntityType2DProfilesUnderView = this._altitudeView.currentEntityType;
        if (currentEntity2DProfilesUnderView) {
            this._update2DViews(currentEntity2DProfilesUnderView, false, currentEntityType2DProfilesUnderView);
        }
    }
    async cancel2PointsAlignment() {
        await this._drawing2dImage2PointAlignmentHandler.cancel();
    }
    async pickSourcePointFor2PointsAlignment(color) {
        return await this._drawing2dImage2PointAlignmentHandler.pickSourcePoint(color);
    }
    async startPickingDestinationPointFor2PointsAlignment(color, callback) {
        return this._drawing2dImage2PointAlignmentHandler.startPickingDestinationPoint(color, callback);
    }
    async endPickingDestinationPointFor2PointsAlignment() {
        return this._drawing2dImage2PointAlignmentHandler.endPickingDestinationPoint();
    }
    async updateDestinationPositionFor2PointsAlignment(localPosition) {
        return this._drawing2dImage2PointAlignmentHandler.updateDestinationPosition(localPosition);
    }
    startDrawing2dImageConstruction(callback) {
        this._drawing2dImageConstructionHandler.start(callback);
    }
    finishDrawing2dImageConstruction() {
        this._drawing2dImageConstructionHandler.end();
    }
    cancelDrawing2dImageConstruction() {
        this._drawing2dImageConstructionHandler.cancel();
    }
    checkDrawing2DImageUnderConstruction() {
        return this._drawing2dImageConstructionHandler.isUnderConstruction();
    }
    async addTileset(id, type, input, tileAssetOption) {
        await this._cesiumView.addTileset(id, type, input, tileAssetOption);
        if (this._altitudeView.currentEntityID) {
            let current2DEntity;
            let current2DEntityType;
            current2DEntity = this._site.getRoad(this._altitudeView.currentEntityID);
            current2DEntityType = AssetType.Road_Data;
            if (!current2DEntity) {
                current2DEntity = this._site.getTrench(this._altitudeView.currentEntityID);
                current2DEntityType = AssetType.Trench_Data;
            }
            if (current2DEntity) {
                this._update2DViews(this._altitudeView.currentEntityID, true, current2DEntityType);
            }
        }
    }
    async addDrawing2dImage(args) {
        await this._site.addReferenceTopology(args, this._cesiumView.viewer, this._cesiumPointSelectorFactory);
        ActivityLogger.log(Activity.REFERENCE_TOPOLOGY_LOADED);
        const currentEntity2DProfilesUnderView = this._altitudeView.currentEntityID;
        const currentEntityType2DProfilesUnderView = this._altitudeView.currentEntityType;
        if (currentEntity2DProfilesUnderView) {
            this._update2DViews(currentEntity2DProfilesUnderView, false, currentEntityType2DProfilesUnderView);
        }
    }
    async addReferenceTopologyFromJSON(assetId, file, args) {
        await this._site.addReferenceTopologyFromJSON(assetId, file, args, this._cesiumView.viewer, this._cesiumPointSelectorFactory);
        ActivityLogger.log(Activity.REFERENCE_TOPOLOGY_LOADED);
        const currentEntity2DProfilesUnderView = this._altitudeView.currentEntityID;
        const currentEntityType2DProfilesUnderView = this._altitudeView.currentEntityType;
        if (currentEntity2DProfilesUnderView) {
            this._update2DViews(currentEntity2DProfilesUnderView, false, currentEntityType2DProfilesUnderView);
        }
    }
    getJSONOfReferenceTopology(id) {
        return this._site.getJSONOfReferenceTopology(id);
    }
    removeTileset(id, type) {
        const tileSet = this._site.getTerrain(id, type);
        if (tileSet) {
            tileSet.dispose();
            if (type === AssetType.Point_Cloud) {
                this._site.deletePointCloud(id);
            }
            else if (type === AssetType.Design_Data) {
                this._site.deleteDesignData(id);
            }
            if (this._altitudeView.currentEntityID) {
                this._update2DViews(this._altitudeView.currentEntityID, true, type);
            }
        }
        else {
            console.warn(`${type} tileset with id ${id} not found`);
        }
    }
    // /**
    //  * Enables the picking on cesium view
    //  * @param pickingCallback
    //  */
    //   public enablePickingOnCesiumView(pickingCallback: PickingCallback) {
    //   this._cesiumView.enablePicking(pickingCallback);
    // }
    // /**
    //  * Disables the picking on cesium view
    //  */
    //   public disablePickingOnCesiumView() {
    //   this._cesiumView.disablePickingOnCesiumView();
    // }
    /**
     *
     * @param longitude
     * @param latitude
     * @param color Should be a css string
     * @returns ID of the PIN Added
     */
    addPin(longitude, latitude, color) {
        return this._pinManager.addPin(this._cesiumView.viewer, longitude, latitude, color);
    }
    removePin(id) {
        return this._pinManager.removePin(this._cesiumView.viewer, id);
    }
    updatePin(id, data) {
        return this._pinManager.updatePin(id, data);
    }
    focusPin(id) {
        return this._pinManager.focusPin(id);
    }
    makeTerrainTransparent(status, near, nearTransparancyValue, far, farTransparancyValue) {
        this._cesiumView.makeTerrainTransparent(status, near, nearTransparancyValue, far, farTransparancyValue);
    }
    toggleGlobeVisibility(visibility) {
        this._cesiumView.toggleGlobeVisibility(visibility);
    }
    clear() {
        this._cesiumView.clear();
        this._measurementManager.clearAll();
    }
    focusSiteLocation(longitude, latitude) {
        this._cesiumView.viewerCamera.flyToLongLatHeight({
            longitude,
            latitude,
            height: 600,
        });
    }
    zoomInSite() {
        this._cesiumView.viewerCamera.zoomIn();
    }
    zoomOutSite() {
        this._cesiumView.viewerCamera.zoomOut();
    }
    flyToLongLatHeight({ longitude, latitude, height, duration = 0, }) {
        this._cesiumView.viewerCamera.flyToLongLatHeight({
            longitude,
            latitude,
            height,
            duration,
        });
    }
    showGlobe() {
        this._cesiumView.viewerCamera.showGlobalView();
    }
    requestRender() {
        this._cesiumView.requestRender();
    }
    async updateSlopeBreaksFromLongitudinalView(roadId, data) {
        const road = this._site.getRoad(roadId);
        if (road &&
            this._roadHandler.editHandler.roadUnderEdit &&
            road.id === this._roadHandler.editHandler.roadUnderEdit.id) {
            await this._roadHandler.editHandler.updateSlopeBreaksFromLongitudinalView(data);
        }
    }
    // public getDMSCoordinates(pageX: number, pageY: number) {
    //     return this._cesiumView.getDMSCoordinates(pageX, pageY);
    // }
    getXYZCoordinates(offsetX, offsetY) {
        return this._cesiumView.getXYZCoordinates(offsetX, offsetY);
    }
    clearCommandManager() {
        this._commandManager.clear();
    }
    getSlopeBreaks(roadId) {
        const road = this._site.getRoad(roadId);
        if (road) {
            const length2d = road.getLengthOnDatum();
            return road.verticalAlignmentData.map((v) => ({
                percentage: v.horizontalDistance / length2d,
                height: v.elevation,
                isActive: v.isActive,
                associatedControlPointId: v.associatedIPPointId,
            }));
        }
    }
    getSlopeBreakPointInfoOnRoad(roadId, slopeBreakIndex) {
        const road = this._site.getRoad(roadId);
        if (road) {
            const res = road.getSlopeBreakPointAndHeight(slopeBreakIndex);
            return res;
        }
    }
    // AI road
    /**
     * Promise is resolved when user clicks on Finish Drawing context menu option.
     * The data contains array of following object :
     * { x: number, y: number, z: number, heightFromPointCloud: number, heightFromDesignData: number }
     * @returns Promise with AIRoadPreviewData
     */
    createAIRoadPreview() {
        return this._cesiumView.startAIRoadPreviewCreation();
    }
    enableDragForAIRoadPreview() {
        this._cesiumView.enableDraggingForAIRoadPreview();
    }
    updateAIRoadPreviewPointAtIndex(PointIndex, position) {
        this._cesiumView.updateAIRoadPreviewPointAtIndex(PointIndex, position);
    }
    setAIRoadPreviewCallback(callback) {
        this._cesiumView.setAIRoadPreviewCallback(callback);
    }
    removeAIRoadPreview() {
        this._cesiumView.deleteAIRoadPreview();
    }
    async pickPointsForRoadConstruction_AI(numberOfPointsToPick = 2, usePointCloudPriority = false) {
        // 仮設道路AI チャット導線でのみ、設計データの表面ではなく点群（地盤）上を
        // 選択するために点群優先セレクタを使う。他導線（2D図面配置・PoC 等）は
        // 従来の深度ピックを維持する。
        const selector = usePointCloudPriority
            ? this._cesiumPointSelectorFactory.getPointSelector(CesiumPointSelectorType.PriorityBased)
            : undefined;
        return this._cesiumView.pickNumberOfPoints(numberOfPointsToPick, selector);
    }
    // --- 仮設道路AI 中継点ピック（連続選択・道路は生成しない）
    enableRoadAiPointPick(onFinish, maxPoints = 7, onLimitExceeded) {
        this._roadAiPointPickHandler.start(onFinish, maxPoints, onLimitExceeded);
    }
    finishRoadAiPointPick() {
        this._roadAiPointPickHandler.finish();
    }
    cancelRoadAiPointPick() {
        this._roadAiPointPickHandler.cancel();
    }
    undoRoadAiPointPick() {
        this._roadAiPointPickHandler.undo();
    }
    redoRoadAiPointPick() {
        this._roadAiPointPickHandler.redo();
    }
    checkRoadAiPointPickUnderway() {
        return this._roadAiPointPickHandler.isActive();
    }
    /*
     * Returns length of the road
     * @param roadID
     */
    getLengthOfRoad(roadID) {
        const road = this._site.getRoad(roadID);
        return road ? road.getLength() : undefined;
    }
    getControlPointIndexById(roadId, pointId) {
        return this._cesiumView.getControlPointIndexById(roadId, pointId);
    }
    isRoadLoaded(roadId) {
        const road = this._site.getRoad(roadId);
        return !!road;
    }
    // public getPathPointDistances(): number[] {
    //   return this._cesiumView.getPathPointDistances();
    // }
    getRoadPointLength(roadID) {
        const road = this._site.getRoad(roadID);
        if (!road) {
            return;
        }
        return this._roadHandler.getRoadPointLength(road);
    }
    /**
     * Returns the object containing minimum slope value and maximum slope value of the road
     * @param roadID
     * @returns
     */
    getRoadSlopeInfo(roadID) {
        return this._cesiumView.getRoadSlopeInfo(roadID);
    }
    /**
     * Returns the object containing minimum elevation and maximum elevation of the road
     * @param roadID
     * @returns
     */
    getRoadHeightInfo(roadID) {
        return this._cesiumView.getRoadHeightInfo(roadID);
    }
    getRoadMeshGeometry(roadId) {
        const road = this._site.getRoad(roadId);
        if (!road) {
            return;
        }
        return this._roadHandler.getMeshGeometry(road);
    }
    getPolylineVolumeGeometry(roadId) {
        const road = this._site.getRoad(roadId);
        if (!road)
            return;
        // TODO_ROAD
        // return this._roadHandler.getPolylineVolumeGeometry(road);
    }
    /**
     * Function returns Control point coordinates
     * @param roadID Flat ground ID
     * @param controlPointId Flat ground Control point ID whose coordinates we require
     * @returns Cesium.Cartesian3 values of control point
     */
    getCtrlPointCordiOnRoad(roadID, controlPointId) {
        const road = this._site.getRoad(roadID);
        if (!road) {
            return;
        }
        const point = road.getControlPointPos(controlPointId);
        return point ? { x: point.x, y: point.y, z: point.z } : undefined;
    }
    /**
     * This function will calculate volume of Road
     * @param roadID ID of Road whose volume to calculate
     * @param cellX Optional as considered unit grid size 0.5 as required unit area for grid is 0.25 sq.m
     * @param cellY Optional as considered unit grid size 0.5 as required unit area for grid is 0.25 sq.m
     * @param cellZ Optional as considered unit grid size 0.5 as required unit area for grid is 0.25 sq.m
     * @returns Fill and Cut Volume in cu. meter.
     */
    async volumeCalculationForRoad(roadID) {
        const road = this._site.getRoad(roadID);
        if (!road) {
            throw new Error(`Road not found with ID ${roadID}`);
        }
        const result = await this._roadHandler.volumeCalculationForRoad(road);
        return { fillVolume: result.fillVolume, cutVolume: result.cutVolume };
    }
    /**
     * Saves Road object as JSON string and return that string
     * @param roadID
     * @returns
     */
    getRoadJSON(roadID) {
        const road = this._site.getRoad(roadID);
        if (!road) {
            return;
        }
        const data = this._roadHandler.getRoadInfo(road);
        return data;
    }
    /**
     * Returns Road Linear data as JSON string and return that string
     * @param roadInfoObject contains necessary params to generate Linear data JSON for road.
     * @returns
     */
    getRoadLinearData(roadInfoObject) {
        const road = this._site.getRoad(roadInfoObject.roadID);
        if (!road) {
            return;
        }
        const data = this._roadHandler.getRoadLinearData(road, roadInfoObject);
        return data;
    }
    /**
     * Saves trench object as JSON string and return that string
     * @param trenchID
     * @returns
     */
    getTrenchJSON(trenchID) {
        const data = this._trenchHandler.getTrenchInfo(trenchID);
        return data;
    }
    /**
     * Returns Trench Linear data as JSON string and return that string
     * @param trenchInfo contains params for generating Linear data of trench
     * @returns
     */
    getTrenchLinearData(trenchInfo) {
        const data = this._trenchHandler.getTrenchLinearData(trenchInfo);
        return data;
    }
    // Creation
    /**
     * Creates Road using json string and return road id
     * @param jsonString
     * @returns
     */
    async loadRoadInfo(roadData, stationInterval, updateInListCallback) {
        const road = await this._roadHandler.loadRoadInfo(roadData, stationInterval);
        updateInListCallback && updateInListCallback(road.id);
        return road.id;
    }
    /**
     * Creates Trench using json string and return trench id
     * @param jsonString
     * @returns
     */
    async loadTrenchJSON(data, stationInterval, updateInListCallback) {
        const trenchData = await this._trenchHandler.loadTrenchInfo(data, stationInterval);
        updateInListCallback && updateInListCallback(trenchData.id);
        return trenchData.id;
    }
    /**
     * This function will create a road with provided array of control points of road
     * @param controlPointArray Array of control points
     */
    createRoadFromPoints(controlPointArray, callback, associatePointCloudId) {
        const associatedTerrain = this._site.getTerrain(associatePointCloudId, AssetType.Point_Cloud);
        if (!associatedTerrain) {
            throw new Error("Associated terrain not found");
        }
        const associatedRubbingDestination = {
            id: associatePointCloudId,
            target: ERubbingDestination.POINT_CLOUD,
        };
        const road = this._roadHandler.createRoadFromPoints(controlPointArray, associatedTerrain, associatedRubbingDestination);
        this._update2DViews(road.id, true, AssetType.Road_Data);
        callback(road.id);
    }
    /**
     * AI が生成した経路から仮設道路を作成し、作成後に callback へ道路 ID を渡す。
     * 点群が見つからない場合を含め、失敗はすべて返り値の Promise の reject で通知する。
     */
    async createRoadFromPointsWithAI(roadPoints, controlPointArray, callback, associatePointCloudId) {
        const associatedTerrain = this._site.getTerrain(associatePointCloudId, AssetType.Point_Cloud);
        if (!associatedTerrain) {
            throw new Error("Associated terrain not found");
        }
        const associatedRubbingDestination = {
            id: associatePointCloudId,
            target: ERubbingDestination.POINT_CLOUD,
        };
        const road = await this._roadHandler.createRoadFromPointsWithAI(roadPoints, controlPointArray, associatedTerrain, associatedRubbingDestination);
        this._update2DViews(road.id, true, AssetType.Road_Data);
        callback(road.id);
    }
    /**
     * This function will create a trench with provided array of control points of trench
     * @param controlPointArray Array of control points
     */
    async createTrenchFromPoints(controlPointArray, callback, associatePointCloudId) {
        const associatedTerrain = this._site.getTerrain(associatePointCloudId, AssetType.Point_Cloud);
        if (!associatedTerrain) {
            throw new Error("Associated terrain not found");
        }
        const associatedRubbingDestination = {
            id: associatePointCloudId,
            target: ERubbingDestination.POINT_CLOUD,
        };
        const trench = await this._trenchHandler.createTrenchFromPoints(controlPointArray, associatedTerrain, associatedRubbingDestination);
        this._update2DViews(trench.id, true, AssetType.Road_Data);
        callback(trench.id);
    }
    enablePathCreation(callback, associatedTerrain, associatedRubbingDestination, associatePointCloudId, associateDrawing2dImageId, pointLimit) {
        const _associatedTerrain = this._site.getTerrain(associatedTerrain.id, associatedTerrain.terrainType);
        if (!_associatedTerrain) {
            throw new Error("Associated terrain not found");
        }
        this._roadHandler.startRoadConstruction({ onFinish: callback }, associatedRubbingDestination, _associatedTerrain);
    }
    enablePathCreationWithClickCount(callback, associatedTerrain, associatedRubbingDestination, clickCount, associatePointCloudId, associateDrawing2dImageId) {
        const _associatedTerrain = this._site.getTerrain(associatedTerrain.id, associatedTerrain.terrainType);
        if (!_associatedTerrain) {
            throw new Error("Associated terrain not found");
        }
        this._roadHandler.startRoadConstructionWithClickCount({ onFinish: callback }, _associatedTerrain, associatedRubbingDestination, clickCount, associatePointCloudId, associateDrawing2dImageId);
    }
    /**
     * Add Control Point on Road before the control point on which this function is called
     * @param roadID ID Of the road to update the settings
     *
     */
    addControlPointOnRoad(roadID, controlPointId) {
        const road = this._site.getRoad(roadID);
        if (road &&
            this._roadHandler.editHandler.roadUnderEdit &&
            road.id === this._roadHandler.editHandler.roadUnderEdit.id) {
            this._roadHandler.editHandler.addControlPointOnRoad(controlPointId);
        }
    }
    // @needsRequestRender()
    // public addControlPointOnRoadSegment(
    // 	roadId: string,
    // 	segmentId: string,
    // 	point: Cartesian3,
    // ) {
    // 	const road = this._site.getRoad(roadId);
    // 	if (
    // 		road &&
    // 		this._roadHandler.editHandler.roadUnderEdit &&
    // 		road.id === this._roadHandler.editHandler.roadUnderEdit.id
    // 	) {
    // 		this._roadHandler.editHandler.addControlPointOnRoadSegment(
    // 			segmentId,
    // 			point,
    // 		);
    // 	}
    // }
    /**
     * This Function will let you add a new control point on the given point on the center line
     * @param roadID unique ID of the road
     * @param localPoint point on center line of the road in local space
     */
    addControlPointOnCenterLineOfRoad(roadId, localPoint) {
        const road = this._site.getRoad(roadId);
        if (road &&
            this._roadHandler.editHandler.roadUnderEdit &&
            road.id === this._roadHandler.editHandler.roadUnderEdit.id) {
            this._roadHandler.editHandler.addControlPointOnCenterLine(localPoint);
        }
    }
    /**
     * Road Extension function will extend the road by one control point.
     * This function can also be called by pressing 'Shift + T' on keyboard in edit mode.
     * @param roadID unique ID of the road
     */
    // @needsRequestRender()
    // public roadExtension(roadID: string) {
    // 	const road = this._site.getRoad(roadID);
    // 	if (
    // 		road &&
    // 		this._roadHandler.editHandler.roadUnderEdit &&
    // 		road.id === this._roadHandler.editHandler.roadUnderEdit.id
    // 	) {
    // 		this._roadHandler.editHandler.roadExtension();
    // 	}
    // }
    /**
     * This Function will check whether there is any road under creation.
     * @returns True or False for road under creation or not.
     */
    checkRoadUnderCreation() {
        return this._roadHandler.checkRoadUnderCreation();
    }
    // Edit
    /**
     * Starts or ends road edit mode.
     * @param updateCallback Called after edits and section selections. `sectionMarkerLabel` is the selected
     * section's name; `undefined` means the label is unchanged (not cleared).
     */
    enableRoadEdit(status, roadID, updateCallback, onDoubleClickOnSlopeTextCallback) {
        const road = this._site.getRoad(roadID);
        if (!road) {
            return;
        }
        if (!status) {
            this.clear2DViews();
        }
        if (status) {
            this._roadHandler.startRoadEdit(road, {
                onStart: () => {
                    this._update2DViews(road.id, true, AssetType.Road_Data);
                },
                onProgress: (id, _distFromStartPoint, sectionMarkerLabel) => {
                    if (typeof id !== "string")
                        return;
                    this._update2DViews(id, false, AssetType.Road_Data);
                    updateCallback(roadID, typeof sectionMarkerLabel === "string"
                        ? sectionMarkerLabel
                        : undefined);
                },
            });
        }
        else {
            this._roadHandler.finishRoadEdit();
            if (road.isCenterLineVisible) {
                this._lineWorkProfileHandler.renderProfileData(road.alignment);
            }
        }
        this._altitudeView.enableEntityEdit(status, roadID, async (id, data) => {
            await this._roadHandler.editHandler.updateSlopeBreaksFromLongitudinalView(data);
            this._update2DViews(road.id, true, AssetType.Road_Data);
            updateCallback(id);
        }, (id, hit) => {
            if (road &&
                this._roadHandler.editHandler.roadUnderEdit &&
                road.id === this._roadHandler.editHandler.roadUnderEdit.id) {
                this._roadHandler.editHandler.selectSectionHit(hit);
                this._cesiumView.viewer.scene.requestRender(); // this cannot be covered with decorator
            }
        }, AssetType.Road_Data, onDoubleClickOnSlopeTextCallback);
    }
    // @needsRequestRender()
    // public updateSegmentToStraight(roadId: string, segmentId: string) {
    // 	const road = this._site.getRoad(roadId);
    // 	if (
    // 		road &&
    // 		this._roadHandler.editHandler.roadUnderEdit &&
    // 		road.id === this._roadHandler.editHandler.roadUnderEdit.id
    // 	) {
    // 		const segment = road.getSegmentById(segmentId);
    // 		const settings = segment?.settings;
    // 		const settingsClone = JSON.parse(JSON.stringify(settings));
    // 		settingsClone.type = SegmentType.STRAIGHT;
    // 		this._roadHandler.editHandler.updateSegmentSetting(
    // 			segmentId,
    // 			settingsClone,
    // 		);
    // 	}
    // }
    // @needsRequestRender()
    // public updateSegmentToCurve(roadId: string, segmentId: string) {
    // 	const road = this._site.getRoad(roadId);
    // 	if (
    // 		road &&
    // 		this._roadHandler.editHandler.roadUnderEdit &&
    // 		road.id === this._roadHandler.editHandler.roadUnderEdit.id
    // 	) {
    // 		const segment = road.getSegmentById(segmentId);
    // 		const settings = segment?.settings;
    // 		const settingsClone = JSON.parse(JSON.stringify(settings));
    // 		settingsClone.type = SegmentType.CURVE;
    // 		this._roadHandler.editHandler.updateSegmentSetting(
    // 			segmentId,
    // 			settingsClone,
    // 		);
    // 	}
    // }
    updateRoadHorizontalAlignmentType(roadId, horizontalAlignmentType) {
        const road = this._site.getRoad(roadId);
        if (road) {
            this._roadHandler.editHandler.updateHorizontalAlignmentType(horizontalAlignmentType);
        }
    }
    async updateAlignmentStationInterval(alignmentId, stationInterval) {
        const alignment = this._site.getCesiumRenderableAlignment(alignmentId);
        if (alignment) {
            await alignment.updateStationInterval(stationInterval);
            this._refreshSectionSelectionIfEditing(alignmentId);
        }
    }
    /** Re-resolves the section selection of the road/trench under edit, e.g. after its stations were rebuilt. */
    _refreshSectionSelectionIfEditing(id) {
        if (this._roadHandler.editHandler.roadUnderEdit?.id === id) {
            this._roadHandler.editHandler.refreshSectionSelection();
        }
        if (this._trenchHandler.editHandler.trenchUnderEdit?.id === id) {
            this._trenchHandler.editHandler.refreshSectionSelection();
        }
    }
    getCorrespondingProjectedDistFromStart(roadId, distanceFromStart) {
        const road = this._site.getRoad(roadId);
        if (road) {
            return road.getCorrespondingProjectedDistFromStart(distanceFromStart);
        }
    }
    getSegmentByPointForTrench(trenchId, point) {
        const trench = this._site.getTrench(trenchId);
        if (trench) {
            return trench.getSegmentByPoint(point);
        }
    }
    updateTrenchHorizontalAlignmentType(trenchId, horizontalAlignmentType) {
        const trench = this._site.getTrench(trenchId);
        if (trench &&
            this._trenchHandler.editHandler.trenchUnderEdit &&
            trench.id === this._trenchHandler.editHandler.trenchUnderEdit.id) {
            this._trenchHandler.editHandler.updateHorizontalAlignmentType(horizontalAlignmentType);
        }
    }
    // @needsRequestRender()
    // public updateBendToCircle(roadId: string, controlPointId: string) {
    // 	const road = this._site.getRoad(roadId);
    // 	if (
    // 		road &&
    // 		this._roadHandler.editHandler.roadUnderEdit &&
    // 		road.id === this._roadHandler.editHandler.roadUnderEdit.id
    // 	) {
    // 		this._roadHandler.editHandler.updateBendToCurveAtControlPoint(
    // 			controlPointId,
    // 		);
    // 	}
    // }
    // @needsRequestRender()
    // public updateBendToCircleForTrench(trenchId: string, controlPointId: string) {
    // 	const trench = this._site.getTrench(trenchId);
    // 	if (
    // 		trench &&
    // 		this._trenchHandler.editHandler.trenchUnderEdit &&
    // 		trench.id === this._trenchHandler.editHandler.trenchUnderEdit.id
    // 	) {
    // 		this._trenchHandler.editHandler.updateBendToCurveAtControlPoint(
    // 			controlPointId,
    // 		);
    // 	}
    // }
    /**
     * Function to updates coordinates values of control points
     * @param roadID Road ID
     * @param controlPointID Road Control point ID whose coordinates we will update
     * @param x by this x value we need to update control point value
     * @param y by this y value we need to update control point value
     * @param z by this z value we need to update control point value
     */
    updateCtrlPointCordiOnRoad(roadID, controlPointID, point) {
        const road = this._site.getRoad(roadID);
        if (road &&
            this._roadHandler.editHandler.roadUnderEdit &&
            road.id === this._roadHandler.editHandler.roadUnderEdit.id) {
            this._roadHandler.editHandler.updateCtrlPointCordiOnRoad(controlPointID, point);
        }
    }
    /**
     * Update the settings for existing road
     * @param roadID ID Of the road to update the settings
     * @param settings setting's object
     */
    async updateRoadSettings(roadID, settings) {
        const road = this._site.getRoad(roadID);
        if (!road) {
            return;
        }
        // settings are in different format
        // convert it to IRoadSetting
        // const formattedSettings: IRoadSetting = RoadSavedData.fillEmptyByDefaultRoadSetting(RoadSavedData.fromOldRoadSettingsData(settings));
        await this._roadHandler.updateRoadSettings(road, settings);
        if (this._altitudeView.currentEntityID) {
            // const sectionPlaneDistance = this._roadHandler.editHandler.sectionPlaneDistanceFromStart;
            this._update2DViews(roadID, false, AssetType.Road_Data);
        }
    }
    updateRoadMaterialSettings(roadID, commonMaterial, embarkmentMaterial, cutMaterial) {
        const road = this._site.getRoad(roadID);
        if (!road) {
            return;
        }
        this._roadHandler.updateRoadMaterial(road, commonMaterial, embarkmentMaterial, cutMaterial);
    }
    /**
     * Sets a top-down (2D) camera view centered on the axis-aligned bounding box (AABB)
     * of the specified object.
     *
     * The method attempts to retrieve the AABB from the object using the following priority:
     * 1. Trench
     * 2. FlatGround
     * 3. Road
     *
     * If no AABB can be determined for the given object ID, a warning is logged and the method exits.
     *
     * @param {string} objectId - The ID of the object to center the camera view on
     */
    setTopViewCameraToObject(objectId) {
        const aabb_wgs84 = this._site.getTrench(objectId)?.trenchAABB_WGS84() ??
            this._site.getFlatGround(objectId)?.flatGroundAABB_WGS84() ??
            this._site.getRoad(objectId)?.roadAABB_WGS84() ??
            this._site.findSolidMesh(objectId)?.solidMeshAABB_WGS84();
        if (!aabb_wgs84) {
            console.warn(`Could not determine AABB for objectId=${objectId}`);
            return;
        }
        const { minimum: { x: minX, y: minY }, maximum: { x: maxX, y: maxY }, center: { z }, } = aabb_wgs84;
        const wgs84Corners = [
            [minX, maxY], // topLeft
            [maxX, maxY], // topRight
            [maxX, minY], // bottomRight
            [minX, minY], // bottomLeft
        ].map(([x, y]) => new Cartesian3(x, y, z));
        this._cesiumView.viewerCamera.set2DView(...wgs84Corners);
    }
    /**
     * This Function will Disable Road Creation.
     * @param roadUnderConstruction Pass a parameter of type 'Road' to this function to disable road creation.
     */
    finishRoadConstruction() {
        // this._cesiumView.disableRoadCreation(roadUnderConstruction);
        this._roadHandler.finishRoadConstruction();
    }
    /**
     * This Function will Disable Road Creation.
     */
    //  public disableRoadCreationHandler() {
    //     this._cesiumView.disableRoadCreationHandler();
    // }
    cancelRoadCreation() {
        // this._cesiumView.cancelRoadCreation();
        this._roadHandler.cancelRoadConstruction();
    }
    /**
     * Deletes the road mesh, control points, control lines and polyline attached with road
     * @param roadID unique ID of the road
     */
    deleteRoad(roadID) {
        const road = this._site.getRoad(roadID);
        if (!road) {
            return;
        }
        if (road.constructionState === ConstructionState.UNDER_EDIT) {
            this._roadHandler.cancelRoadEdit();
        }
        this._site.deleteRoad(roadID);
        this._altitudeView.deleteEntityPath(roadID);
        this._sectionView.deleteEntityPath(roadID);
        this._lineWorkCrossSectionHandler.dispose(roadID);
    }
    // road edit
    removeControlPoint(roadID, controlPointId) {
        const road = this._site.getRoad(roadID);
        if (road &&
            this._roadHandler.editHandler.roadUnderEdit &&
            road.id === this._roadHandler.editHandler.roadUnderEdit.id) {
            this._roadHandler.editHandler.removeControlPoint(controlPointId);
        }
    }
    async removeSelectedSlopeBreak(roadID) {
        const updateSlopeBreaks = this._altitudeView.removeSelectedSlopeBreak(roadID);
        if (updateSlopeBreaks) {
            await this.updateSlopeBreaksFromLongitudinalView(roadID, updateSlopeBreaks);
        }
    }
    /**
     * Force-recalculate road geom
     * @param roadId
     */
    recalculateRoadGeometry(roadId) {
        this._cesiumView.recalculateRoadGeometry(roadId);
    }
    // FlatGround
    async pickFlatGroundEntities(mousePos) {
        return await this._flatGroundHandler.editHandler.pickFlatGroundEntities(mousePos);
    }
    /**
     * API used to zoom in to the flat ground attached with flatGroundId
     * @param flatGroundId
     */
    fitToFlatGround(flatGroundId) {
        this._cesiumView.fitToFlatGround(flatGroundId);
    }
    /**
     * API used to zoom in to the trench attached with trenchId
     * @param trenchId
     */
    fitToTrench(trenchId) {
        this._trenchHandler.fitToTrench(trenchId);
        const trench = this._site.getTrench(trenchId);
        if (!trench) {
            return;
        }
        if (trench.isCenterLineVisible) {
            this._lineWorkProfileHandler.renderProfileData(trench.alignment);
        }
        this._update2DViews(trenchId, true, AssetType.Trench_Data);
    }
    /**
     * Enables the flat ground creation mode when status is set to true and disbale the mode when status is set to false
     * @param status
     * @param callback (flatGroundID) => {console.log(flatGroundID)}
     */
    enableFlatGroundCreation(callback, associatedTerrain, associatedRubbingDestination) {
        const _associatedTerrain = this._site.getTerrain(associatedTerrain.id, associatedTerrain.terrainType);
        if (!_associatedTerrain) {
            throw new Error("Associated terrain not found");
        }
        this._flatGroundHandler.startFlatGroundConstruction({ onFinish: callback }, _associatedTerrain, associatedRubbingDestination);
    }
    /**
     * Removes the flat ground from the viewer if present
     * @param polygonID
     */
    removeFlatground(polygonID) {
        const flatground = this._site.getFlatGround(polygonID);
        if (!flatground) {
            return;
        }
        if (flatground.constructionState === ConstructionState.UNDER_EDIT) {
            this._flatGroundHandler.cancelFlatGroundEdit();
        }
        this._site.deleteFlatGround(polygonID);
        ActivityLogger.log(Activity.FLATGROUNDDATA_LOADED);
        this._update2DViewsIfEntitySelected();
    }
    /**
     * Function returns Control point coordinates
     * @param flatGroundID Flat ground ID
     * @param flatGroundPointID Flat ground Control point ID whose coordinates we require
     * @returns Cesium.Cartesian3 values of control point
     */
    getCtrlPointCordiOnFlatGround(flatGroundID, flatGroundPointID) {
        return this._cesiumView.getCtrlPointCordiOnFlatGround(flatGroundID, flatGroundPointID);
    }
    /**
     * Function to updates coordinates values of control points
     * @param flatGroundID Flat ground ID
     * @param flatGroundPointID Flat ground Control point ID whose coordinates we will update
     * @param x by this x value we need to update control point value
     * @param y by this y value we need to update control point value
     * @param z by this z value we need to update control point value
     */
    async updateCtrlPointCordiOnFlatGround(flatGroundID, flatGroundPointID, point) {
        const flatGround = this._site.getFlatGround(flatGroundID);
        if (flatGround &&
            this._flatGroundHandler.editHandler.flatGroundUnderEdit &&
            flatGround.id ===
                this._flatGroundHandler.editHandler.flatGroundUnderEdit.id) {
            point &&
                (await this._flatGroundHandler.editHandler.updateCtrlPointCordinateOnFlatGround(flatGroundPointID, point));
        }
    }
    /**
     * Enables the flat ground point drag mode when status is set true and disables it when status is set to false
     * @param status
     * @param flatGroundID
     */
    enableFlatGroundEdit(status, flatGroundId, updateCallback) {
        const flatground = this._site.getFlatGround(flatGroundId);
        if (!flatground) {
            return;
        }
        if (status) {
            this._flatGroundHandler.startFlatGroundEdit(flatground, {
                onProgress: (id) => {
                    if (typeof id !== "string")
                        return;
                    updateCallback(id);
                },
            });
        }
        else {
            this._flatGroundHandler.finishFlatGroundEdit();
        }
    }
    /**
     * Removes the point from flat ground area
     * @param flatGroundID
     * @param flatGroundPointID
     */
    removePointFromFlatGround(flatGroundID, flatGroundPointID) {
        const flatGround = this._site.getFlatGround(flatGroundID);
        if (flatGround &&
            this._flatGroundHandler.editHandler.flatGroundUnderEdit &&
            flatGround.id ===
                this._flatGroundHandler.editHandler.flatGroundUnderEdit.id) {
            this._flatGroundHandler.editHandler.removePointFromFlatGround(flatGroundPointID);
        }
    }
    /**
     * This Fucntion will add a new reference point in mid when left click on previewed midpoints.
     * @param flatGroundID Flat Ground ID on which function should be called
     */
    //   public addMidPointsForFlatGround(flatGroundID: string, callback: (id: string) => void) {
    //   this._cesiumView.addMidPointsForFlatGround(flatGroundID, (id) => {
    //     callback(id);
    //   });
    // }
    addFlatGroundControlPointOnOutline(flatGroundId, posOnOutline) {
        const flatGround = this._site.getFlatGround(flatGroundId);
        if (flatGround &&
            this._flatGroundHandler.editHandler.flatGroundUnderEdit &&
            flatGround.id ===
                this._flatGroundHandler.editHandler.flatGroundUnderEdit.id) {
            this._flatGroundHandler.editHandler.addFlatGroundControlPointOnOutline(posOnOutline);
        }
    }
    /**
     * Used to drag Flat Ground Point vertically by dragging the arrow show when called this function
     * Or by double clicking on arrows to enter desiare value
     * @param status True/False
     * @param flatGroundID Flat Ground ID for which this function is to call.
     */
    setFlatGroundPointToVerticalDrag(flatGroundId) {
        const flatGround = this._site.getFlatGround(flatGroundId);
        if (flatGround &&
            this._flatGroundHandler.editHandler.flatGroundUnderEdit &&
            flatGround.id ===
                this._flatGroundHandler.editHandler.flatGroundUnderEdit.id) {
            this._flatGroundHandler.editHandler.setFlatGroundPointToVerticalDrag();
        }
    }
    setFlatGroundPointToHorizontalDrag(flatGroundID) {
        const flatGround = this._site.getFlatGround(flatGroundID);
        if (flatGround &&
            this._flatGroundHandler.editHandler.flatGroundUnderEdit &&
            flatGround.id ===
                this._flatGroundHandler.editHandler.flatGroundUnderEdit.id) {
            this._flatGroundHandler.editHandler.setFlatGroundPointToHorizontalDrag();
        }
    }
    async updateFlatGroundSettings(flatGroundID, settings) {
        await this._flatGroundHandler.updateFlatGroundSettings(flatGroundID, settings);
    }
    async updateFlatGroundMaterialSettings(flatGroundID, commonMaterial, embankmentMaterial, cutMaterial) {
        await this._cesiumView.updateFlatGroundMaterial(flatGroundID, commonMaterial, embankmentMaterial, cutMaterial);
    }
    /**
     * Re-generates flat ground geometry from current in-memory settings and shows the flat ground.
     * Used when re-showing a flat ground that was hidden during an edit session.
     */
    async restoreFlatGroundAfterHiding(flatGroundID) {
        const flatGround = this._site.getFlatGround(flatGroundID);
        if (!flatGround) {
            return;
        }
        this._cesiumView.toggleFlatGroundVisibility(flatGroundID, true);
        await flatGround.updateSettings(flatGround.settings);
    }
    /**
     * Updates the visibility of the Flat Ground related to provided flatGroundID
     * @param flatGroundID
     * @param show
     */
    toggleFlatGroundVisibility(flatGroundID, show) {
        this._cesiumView.toggleFlatGroundVisibility(flatGroundID, show);
        ActivityLogger.log(Activity.FLATGROUNDDATA_VISIBILITY_UPDATE);
        this._update2DViewsIfEntitySelected();
    }
    /**
     * Export the flatGround's LandXML file
     * @param flatGroundID
     */
    getFlatGroundMeshGeometry(flatGroundID) {
        return this._cesiumView.getMeshGeometryOfFlat(flatGroundID);
    }
    getDesignMeshGeometry(designId) {
        const design = this._site.getDesignData(designId);
        if (!(design instanceof DesignTileSet)) {
            throw new Error(`DesignTileSet not found: ${designId}`);
        }
        return design.geometry;
    }
    /**
     * Start point for creation of flatground center arrow entity
     * @param status Controls the flatground ceneter arrow creation
     * @param flatGroundID
     */
    setFlatGroundToUniformVerticalDrag(flatGroundID) {
        const flatGround = this._site.getFlatGround(flatGroundID);
        if (flatGround &&
            this._flatGroundHandler.editHandler.flatGroundUnderEdit &&
            flatGround.id ===
                this._flatGroundHandler.editHandler.flatGroundUnderEdit.id) {
            this._flatGroundHandler.editHandler.setFlatGroundToUniformDrag();
        }
    }
    /**
     * Saves flat ground object as JSON string and return that string
     * @param id
     * @returns
     */
    getFlatGroundJSON(id) {
        return this._cesiumView.getFlatGroundInfo(id);
    }
    /**
     * Creates flat ground using json string and return flat ground id
     * @param jsonString
     * @returns
     */
    async loadFlatGroundArea(data, updateInListCallback) {
        const flatground = await this._flatGroundHandler.loadFlatGroundInfo(data);
        flatground && updateInListCallback && updateInListCallback(flatground.id);
        ActivityLogger.log(Activity.FLATGROUNDDATA_LOADED);
        this._update2DViewsIfEntitySelected();
        return flatground.id;
    }
    /**
     * This Function will Disable Flat Ground Creation.
     * @param flatGround Pass a parameter of type 'EarthBrainFlatGround' to this function to disable Flat Ground creation.
     */
    //   public disableFlatGroundCreation(flatGround: FlatGround) {
    //   this._cesiumView.disableFlatGroundCreation(flatGround);
    // }
    /**
     * This Function will check whether there is any Flat Ground under creation.
     * @returns True or False for Flat Ground under creation or not.
     */
    checkFlatGroundUnderCreation() {
        return this._flatGroundHandler.checkFlatGroundUnderCreation();
    }
    /**
     * This Function will Disable Flat Ground Creation.
     */
    disableFlatGroundCreationHandler() {
        this._flatGroundHandler.finishFlatGroundConstruction();
    }
    async setFlatGroundPointsUniformHeight(id, height) {
        await this._flatGroundHandler.setFlatGroundPointsUniformHeight(id, height);
    }
    cancelFlatGroundCreation() {
        this._flatGroundHandler.cancelFlatgroundConstruction();
    }
    /**
     * This function will calculate volume of Flat Ground
     * @param flatGroundID ID of Flat Ground whose volume to calculate
     * @param cellX Optional as considered unit grid size 0.5 as required unit area for grid is 0.25 sq.m
     * @param cellY Optional as considered unit grid size 0.5 as required unit area for grid is 0.25 sq.m
     * @param cellZ Optional as considered unit grid size 0.5 as required unit area for grid is 0.25 sq.m
     * @returns Fill and Cut Volume in cu. meter.
     */
    async volumeCalculationForFlatGround(flatGroundID) {
        const result = await this._flatGroundHandler.volumeCalculationForFlatGround(flatGroundID);
        return { fillVolume: result.fillVolume, cutVolume: result.cutVolume };
    }
    calculateSurfaceAreaOfFlatGround(flatGroundID) {
        const result = this._flatGroundHandler.surfaceAreaCalculationForFlatGround(flatGroundID);
        return result;
    }
    getMaxHeightOfFlatGroundCtrlPts(flatGroundID) {
        return this._flatGroundHandler.getMaxHeightOfFlatGroundCtrlPts(flatGroundID);
    }
    enableMeasurementCreation() {
        return new Promise((resolve) => {
            this._cesiumView.pickNumberOfPoints(2).then((points) => {
                resolve({
                    id: createGuid(),
                    start: Cartesian3.pack(points[0], []),
                    end: Cartesian3.pack(points[1], []),
                });
            });
        });
    }
    cancelMeasurement() {
        this._cesiumView.cancelPicking("Point picking cancelled for measurement");
    }
    async loadMeasurement(id, type, data) {
        await this._measurementManager.load(id, type, data);
    }
    async showMeasurement(id, type) {
        await this._measurementManager.show(id, type);
        if (type === MeasurementOperation.CROSS_SECTION) {
            const crossSectionData = this._measurementManager.getCrossSectionData(id);
            if (crossSectionData) {
                this._measurementView.renderData(id, crossSectionData, true);
            }
        }
    }
    async updateMeasurement(id, type) {
        await this._measurementManager.update(id, type);
        if (type === MeasurementOperation.CROSS_SECTION &&
            id === this._measurementView.currentEntityID) {
            const crossSectionData = this._measurementManager.getCrossSectionData(id);
            if (crossSectionData) {
                this._measurementView.renderData(id, crossSectionData, true);
            }
        }
    }
    hideMeasurement(id, type) {
        this._measurementManager.hide(id, type);
        if (type === MeasurementOperation.CROSS_SECTION) {
            this._measurementView.clearView();
        }
    }
    deleteMeasurement(id) {
        this._measurementManager.delete(id);
        if (id === this._measurementView.currentEntityID) {
            this._measurementView.clearView();
        }
    }
    zoomToMeasurement(id, type) {
        return this._measurementManager.zoomTo(id, type);
    }
    renderPoint(point, color) {
        return EarthBrainUtils.addSphere(this._cesiumView.viewer, point, color);
    }
    async renderLocalPoint(point, color) {
        const wgsPoint = await this._site.convertPointsToWGS84([point]);
        return EarthBrainUtils.addSphere(this._cesiumView.viewer, wgsPoint[0], color);
    }
    async renderLocalLine(points, color) {
        const wgsPoints = await this._site.convertPointsToWGS84(points);
        return EarthBrainUtils.createLineEntity(this._cesiumView.viewer, "localLine", wgsPoints, {
            color: color,
            width: 2,
            outlineColor: color,
            outlineWidth: 1,
        });
    }
    async renderPlane(position, normal, color) {
        const wgsPoint = await this._site.convertPointsToWGS84([position]);
        EarthBrainUtils.createPlane(this._cesiumView.viewer, wgsPoint[0], normal, new Cartesian2(200, 200), color);
    }
    async renderRay(origin, direction, color) {
        const endPoint = Cartesian3.add(origin, Cartesian3.multiplyByScalar(direction, 1000, new Cartesian3()), new Cartesian3());
        const wgsPoint = await this._site.convertPointsToWGS84([origin, endPoint]);
        return EarthBrainUtils.createArrow(this._cesiumView.viewer, wgsPoint, 4, color);
    }
    /**
     * Is Road Control Point Removable
     * @param roadId Id of road
     * @param controlPointId Id of control Point
     * @returns Returns true if road control point is removable
     */
    isRoadControlPointRemovable(roadId, controlPointId) {
        const road = this._site.getRoad(roadId);
        return road?.isControlPointRemovable(controlPointId);
    }
    async updateTerrainAssociatedWithObject(objectId, objectType, terrainId, terrainType) {
        const terrain = this._site.getTerrain(terrainId, terrainType);
        if (!terrain) {
            throw new Error(`Terrain not found for ID ${terrainId} and type ${terrainType}`);
        }
        const _updateTerrainAssociatedWithObject = async (objId, objType) => {
            switch (objType) {
                case "road":
                    const road = this._site.getRoad(objId);
                    await road?.updateAssociatedTerrain(terrain);
                    break;
                case "flat-ground":
                    const flatGround = this._site.getFlatGround(objId);
                    await flatGround?.updateAssociatedTerrain(terrain);
                    break;
                case "trench":
                    const trench = this._site.getTrench(objId);
                    await trench?.updateAssociatedTerrain(terrain);
                    break;
            }
        };
        await _updateTerrainAssociatedWithObject(objectId, objectType);
        //Todo : Need to verify requirement
        const childrenObjects = this._site.structureManager.getChildrenNodesInOrder(objectId);
        const parentObjects = this._site.structureManager.getParentAndSiblingNodesInOrder(objectId);
        const uniqueObjects = new Set([...childrenObjects, ...parentObjects]);
        uniqueObjects.forEach(async (obj) => {
            const roadObj = this._site.getRoad(obj.from.id);
            if (roadObj) {
                await _updateTerrainAssociatedWithObject(roadObj.id, "road");
            }
            const flatGroundObj = this._site.getFlatGround(obj.from.id);
            if (flatGroundObj) {
                await _updateTerrainAssociatedWithObject(flatGroundObj.id, "flat-ground");
            }
        });
    }
    async updateRubbingDestinationAssociatedWithObject(objectId, objectType, rubbingDestination) {
        const target = getRaycastableFromRubbingDestination(this._site, rubbingDestination);
        if (!target) {
            console.error("Rubbing Destination not found");
            return;
        }
        const _updateRubbingDestinationAssociatedWithObject = async (objId, objType) => {
            switch (objType) {
                case "road":
                    const road = this._site.getRoad(objId);
                    await road?.updateAssociatedRubbingDestination(rubbingDestination);
                    break;
                case "flat-ground":
                    const flatGround = this._site.getFlatGround(objId);
                    await flatGround?.updateAssociatedRubbingDestination(rubbingDestination);
                    break;
                case "trench":
                    const trench = this._site.getTrench(objId);
                    await trench?.updateAssociatedRubbingDestination(rubbingDestination);
                    break;
            }
        };
        await _updateRubbingDestinationAssociatedWithObject(objectId, objectType);
        //Todo : Need to verify requirement
        const childrenObjects = this._site.structureManager.getChildrenNodesInOrder(objectId);
        const parentObjects = this._site.structureManager.getParentAndSiblingNodesInOrder(objectId);
        const uniqueObjects = new Set([...childrenObjects, ...parentObjects]);
        uniqueObjects.forEach((obj) => {
            const roadObj = this._site.getRoad(obj.from.id);
            if (roadObj) {
                _updateRubbingDestinationAssociatedWithObject(roadObj.id, "road");
            }
            const flatGroundObj = this._site.getFlatGround(obj.from.id);
            if (flatGroundObj) {
                _updateRubbingDestinationAssociatedWithObject(flatGroundObj.id, "flat-ground");
            }
        });
    }
    updateRoadAndFlatLabelColors(color) {
        this._cesiumView.updateRoadAndFlatLabelColors(color);
    }
    getConnectionsInSiteJSON() {
        return this._site.getConnectionsInSiteJSON();
    }
    loadConnectionsInSiteJSON(jsonString) {
        this._site.loadConnectionsInSiteJSON(jsonString);
    }
    // Trench
    enableTrenchCreation(callback, associatedTerrain, associatedRubbingDestination) {
        // this._trenchCreationCallback = callback;
        // this._cesiumView.enableTrenchPathCreation(
        //   associatePointCloudId,
        //   (pathId) => {
        //     this._trenchCreationCallback(pathId);
        //   }
        // );
        const _associatedTerrain = this._site.getTerrain(associatedTerrain.id, associatedTerrain.terrainType);
        if (!_associatedTerrain) {
            throw new Error("Associated terrain not found");
        }
        this._trenchHandler.startTrenchConstruction({ onFinish: callback }, _associatedTerrain, associatedRubbingDestination);
    }
    /**
     * This Function will check whether there is any road under creation.
     * @returns True or False for road under creation or not.
     */
    checkTrenchUnderCreation() {
        return this._trenchHandler.checkTrenchUnderCreation();
    }
    /**
     * Update the settings for existing trench
     * @param trenchID ID Of the trench to update the settings
     * @param settings setting's object
     */
    async updateTrenchSettings(trenchID, settings, rubbingDestination) {
        // settings are in different format
        // convert it to ITrenchSetting
        // const formattedSettings: ITrenchSetting = TrenchSavedData.fillEmptyByDefaultTrenchSetting(TrenchSavedData.fromOldTrenchSettingsData(settings));
        await this._trenchHandler.updateTrenchSettings(trenchID, settings, rubbingDestination);
        if (this._altitudeView.currentEntityID) {
            this._update2DViews(trenchID, false, AssetType.Trench_Data);
        }
    }
    updateTrenchMaterialSettings(trenchID, commonMaterial, cutMaterial) {
        this._trenchHandler.updateTrenchMaterial(trenchID, commonMaterial, cutMaterial);
    }
    /**
     * This Function will finish Trench Creation.
     */
    finishTrenchCreation() {
        this._trenchHandler.finishTrenchConstruction();
    }
    cancelTrenchCreation() {
        this._trenchHandler.cancelTrenchConstruction();
    }
    deleteTrench(trenchId) {
        const trench = this._site.getTrench(trenchId);
        if (!trench)
            return;
        if (trench.constructionState === ConstructionState.UNDER_EDIT) {
            this._trenchHandler.cancelTrenchEdit();
        }
        this._site.deleteTrench(trenchId);
        this._altitudeView.deleteEntityPath(trenchId);
        this._sectionView.deleteEntityPath(trenchId);
    }
    /**
     * Updates the visibility of the trench related to provided trenchID
     * @param trenchID
     * @param show
     */
    setTrenchVisibility(trenchID, show) {
        this._trenchHandler.setTrenchVisibility(trenchID, show);
        const trench = this._site.getTrench(trenchID);
        if (!trench) {
            return;
        }
        if (!show) {
            this._lineWorkProfileHandler.dispose(trench.alignment.id);
        }
        else if (trench.isCenterLineVisible) {
            this._lineWorkProfileHandler.renderProfileData(trench.alignment);
        }
    }
    setTrenchCenterLineVisibility(trenchID, visible) {
        const trench = this._site.getTrench(trenchID);
        if (!trench) {
            return;
        }
        trench.setCenterLineVisibility(visible);
        if (visible) {
            this._lineWorkProfileHandler.renderProfileData(trench.alignment);
        }
        else {
            this._lineWorkProfileHandler.dispose(trench.alignment.id);
        }
    }
    setTrenchIpLineVisibility(trenchId, visible) {
        const trench = this._site.getTrench(trenchId);
        if (!trench) {
            return;
        }
        trench.setIpLineVisibility(visible);
    }
    setTrenchAlignmentVisibility(trenchID, visible) {
        const trench = this._site.getTrench(trenchID);
        if (!trench) {
            return;
        }
        trench.setAlignmentVisibility(visible);
    }
    setTrenchSurfaceVisibility(trenchID, visible) {
        const trench = this._site.getTrench(trenchID);
        if (!trench) {
            return;
        }
        trench.setSurfaceVisibility(visible);
    }
    setTrenchEmbankmentVisibility(trenchID, visible) {
        const trench = this._site.getTrench(trenchID);
        if (!trench) {
            return;
        }
        trench.setEmbankmentVisibility(visible);
    }
    /*
     * Returns length of the trench
     * @param trenchID
     */
    getLengthOfTrench(trenchID) {
        return this._trenchHandler.getLengthOfTrench(trenchID);
    }
    /**
     * Returns the object containing minimum slope value and maximum slope value of the trench
     * @param trenchID
     * @returns
     */
    getTrenchSlopeInfo(trenchID) {
        return this._trenchHandler.getTrenchSlopeInfo(trenchID);
    }
    /**
     * Returns the object containing minimum elevation and maximum elevation of the trench
     * @param trenchID
     * @returns
     */
    getTrenchHeightInfo(trenchID) {
        return this._trenchHandler.getTrenchHeightInfo(trenchID);
    }
    getTrenchMeshGeometry(trenchId) {
        const trench = this._site.getTrench(trenchId);
        if (!trench)
            return;
        return this._trenchHandler.getMeshGeometry(trench);
    }
    getTrenchPolylineVolumeGeometry(trenchId) {
        const trench = this._site.getTrench(trenchId);
        if (!trench)
            return;
        // TODO_TRENCH
        // return this._trenchHandler.getPolylineVolumeGeometry(trench);
    }
    /**
     * Returns the object containing minimum elevation and maximum elevation of the trench
     * @param trenchID
     * @returns
     */
    getTrenchHeightInfoOfSection(trenchID) {
        return this._trenchHandler.getTrenchHeightInfoOfSection(trenchID);
    }
    /**
     * Starts or ends trench edit mode.
     * @param updateCallback Called after edits and section selections. `sectionMarkerLabel` is the selected
     * section's name; `undefined` means the label is unchanged (not cleared).
     */
    enableTrenchEdit(status, trenchID, updateCallback) {
        const trench = this._site.getTrench(trenchID);
        if (!trench) {
            return;
        }
        if (!status) {
            this.clear2DViews();
        }
        if (status) {
            this._trenchHandler.startTrenchEdit(trench, {
                onStart: () => {
                    this._update2DViews(trench.id, true, AssetType.Trench_Data);
                },
                onProgress: (id, _distFromStartPoint, sectionMarkerLabel) => {
                    if (typeof id !== "string")
                        return;
                    this._update2DViews(id, false, AssetType.Trench_Data);
                    updateCallback(trenchID, typeof sectionMarkerLabel === "string"
                        ? sectionMarkerLabel
                        : undefined);
                },
            });
        }
        else {
            this._trenchHandler.finishTrenchEdit();
            if (trench.isCenterLineVisible) {
                this._lineWorkProfileHandler.renderProfileData(trench.alignment);
            }
        }
        this._altitudeView.enableEntityEdit(status, trenchID, async () => {
            // this.updateTrenchSlopeBreaksFromLongitudinalView(trenchID, data);
            // updateCallback(id);
        }, (id, hit) => {
            this._trenchHandler.editHandler.selectSectionHit(hit);
            this._cesiumView.viewer.scene.requestRender(); // this cannot be covered with decorator
        }, AssetType.Trench_Data);
    }
    removeControlPointOnTrench(controlPointId) {
        this._trenchHandler.editHandler.removeControlPoint(controlPointId);
    }
    /**
     * Is Trench Control Point Removable
     * @param trenchId Id of trench
     * @param controlPointId Id of control Point
     * @returns Returns true if trench control point is removable
     */
    isTrenchControlPointRemovable(trenchId, controlPointId) {
        const trench = this._site.getTrench(trenchId);
        return trench?.isControlPointRemovable(controlPointId);
    }
    /**
     * Function to updates coordinates values of control points
     * @param trenchID Trench ID
     * @param controlPointID Trench Control point ID whose coordinates we will update
     * @param x by this x value we need to update control point value
     * @param y by this y value we need to update control point value
     * @param z by this z value we need to update control point value
     */
    updateCtrlPointCordiOnTrench(trenchID, controlPointID, point) {
        const trench = this._site.getTrench(trenchID);
        if (trench &&
            this._trenchHandler.editHandler.trenchUnderEdit &&
            trench.id === this._trenchHandler.editHandler.trenchUnderEdit.id) {
            this._trenchHandler.editHandler.updateCtrlPointCordiOnTrench(controlPointID, point);
        }
    }
    /**
     * Add Control Point on Trench before the control point on which this function is called
     * @param roadID ID Of the trench to update the settings
     *
     */
    addControlPointOnTrench(trenchID, controlPointId) {
        const trench = this._site.getTrench(trenchID);
        if (trench &&
            this._trenchHandler.editHandler.trenchUnderEdit &&
            trench.id === this._trenchHandler.editHandler.trenchUnderEdit.id) {
            this._trenchHandler.editHandler.addControlPointOnTrench(controlPointId);
        }
    }
    addControlPointOnTrenchSegment(trenchId, segmentId, point) {
        const trench = this._site.getTrench(trenchId);
        if (trench &&
            this._trenchHandler.editHandler.trenchUnderEdit &&
            trench.id === this._trenchHandler.editHandler.trenchUnderEdit.id) {
            this._trenchHandler.editHandler.addControlPointOnTrenchSegment(segmentId, point);
        }
    }
    /**
     * This Function will let you add a new control point on the given point on the center line
     * @param trenchID unique ID of the trench
     * @param localPoint point on center line of the trench
     */
    addControlPointOnCenterLineOfTrench(trenchId, localPoint) {
        const trench = this._site.getTrench(trenchId);
        if (trench &&
            this._trenchHandler.editHandler.trenchUnderEdit &&
            trench.id === this._trenchHandler.editHandler.trenchUnderEdit.id) {
            this._trenchHandler.editHandler.addControlPointOnCenterLine(localPoint);
        }
    }
    /**
     * Trench Extension function will extend the trench by one control point.
     * This function can also be called by pressing 'Shift + T' on keyboard in edit mode.
     * @param trenchID unique ID of the trench
     */
    // public trenchExtension(trenchID: string) {
    // 	const trench = this._site.getTrench(trenchID);
    // 	if (
    // 		trench &&
    // 		this._trenchHandler.editHandler.trenchUnderEdit &&
    // 		trench.id === this._trenchHandler.editHandler.trenchUnderEdit.id
    // 	) {
    // 		this._trenchHandler.editHandler.trenchExtension();
    // 	}
    // }
    isTrenchLoaded(roadId) {
        const trench = this._site.getTrench(roadId);
        return !!trench;
    }
    getTrenchPointLength(trenchID) {
        const trench = this._site.getTrench(trenchID);
        if (!trench)
            return;
        return this._trenchHandler.getTrenchPointLength(trench);
    }
    getTrenchPointIDFromIndex(trenchID, index) {
        const trench = this._site.getTrench(trenchID);
        if (!trench)
            return;
        return this._trenchHandler.getTrenchPointIDFromIndex(trench, index);
    }
    /**
     * Function returns Control point coordinates
     * @param trenchID Trench ID
     * @param controlPointId Trench Control point ID whose coordinates we require
     * @returns Cesium.Cartesian3 values of control point
     */
    getCtrlPointCordiOnTrench(trenchID, controlPointId) {
        const trench = this._site.getTrench(trenchID);
        if (!trench)
            return;
        const point = trench.getControlPointPos(controlPointId);
        return point ? { x: point.x, y: point.y, z: point.z } : undefined;
    }
    /**
     * This function will calculate volume of Trench
     * @param trenchID ID of Trench whose volume to calculate
     * @param cellX Optional as considered unit grid size 0.5 as required unit area for grid is 0.25 sq.m
     * @param cellY Optional as considered unit grid size 0.5 as required unit area for grid is 0.25 sq.m
     * @param cellZ Optional as considered unit grid size 0.5 as required unit area for grid is 0.25 sq.m
     * @returns Fill and Cut Volume in cu. meter.
     */
    async volumeCalculationForTrench(trenchID) {
        const trench = this._site.getTrench(trenchID);
        if (!trench) {
            throw new Error(`Trench not found with ID ${trenchID}`);
        }
        const result = await this._trenchHandler.volumeCalculationForTrench(trench);
        return { fillVolume: result.fillVolume, cutVolume: result.cutVolume };
    }
    async loadSIMAFile(simaFile, options) {
        return await this._simaManager.loadSIMA(simaFile, options);
    }
    hasSIMACentralLine(id) {
        return this._simaManager.hasCentralLine(id);
    }
    /**
     * Update SIMA Visibility
     * @param id ID of SIMA
     * @param visible Visibility of SIMA
     */
    setSIMAVisibility(id, visible) {
        if (visible) {
            this._simaManager.show(id);
        }
        else {
            this._simaManager.hide(id);
        }
    }
    zoomToSIMA(id) {
        const sima = this._simaManager.getSIMA(id);
        if (sima) {
            sima.zoomTo();
            // this._lineWorkProfileHandler.renderProfileData(sima);
        }
    }
    zoomToArbitrary(id) {
        const arbitrary = this._site.getArbitraryLine(id);
        if (arbitrary) {
            arbitrary.zoomTo();
            this._lineWorkProfileHandler.renderProfileData(arbitrary);
        }
    }
    /**
     * Remove SIMA
     * @param id id of the SIMA
     */
    removeSIMA(id) {
        this._lineWorkCrossSectionHandler.dispose(id);
        this._lineWorkProfileHandler.dispose(id);
        this._simaManager.removeSIMA(id);
    }
    /**
     * Add DXF data from JSON
     * @param json JSON containing DXF data
     */
    async addDXFFromJSON(id, json) {
        if (json && json.version >= 1) {
            const dxf = await DXFWGS84.fromJSON(id, json, this._cesiumView.viewer, this._site, json.version);
            this._site.addDXFData(id, dxf);
        }
        else {
            const dxf = await EarthbrainDXF.fromJSON(id, json, this._cesiumView.viewer, this._site);
            this._site.addDXFData(id, dxf);
        }
        return id;
    }
    /**
     * Update DXF Visibility
     * @param id ID of DXF
     * @param visible Visibility of DXF
     */
    setDXFVisibility(id, visible) {
        const dxf = this._site.getDXFData(id);
        if (dxf) {
            dxf.setVisibility(visible);
        }
    }
    /**
     * Update DXF Color
     * @param id ID of DXF
     * @param color CSS Color string
     */
    updateDXFColor(id, color) {
        const dxf = this._site.getDXFData(id);
        if (dxf) {
            dxf.updateColor(Color.fromCssColorString(color));
        }
    }
    zoomToDXF(id) {
        const dxf = this._site.getDXFData(id);
        dxf?.zoomTo();
    }
    /**
     * Remove DXF
     * @param id id of the DXF
     */
    removeDXF(id) {
        this._site.deleteDXFData(id);
    }
    // /**
    //  * Update SIMA Visibility
    //  * @param id ID of SIMA
    //  * @param visible Visibility of SIMA
    //  */
    // @needsRequestRender()
    // setSIMAVisibility(id: number, visible: boolean) {
    //     throw new Error("Method not implemented.");
    // }
    // removeSIMA(id: number) {
    //     throw new Error("Method not implemented.");
    // }
    // zoomToSIMA(id: number) {
    //     throw new Error("Method not implemented.");
    // }
    async adjustHeightOfDXFData(id, assetType, assetID) {
        const dxf = this._site.getDXFData(id);
        let asset;
        if (assetType == AssetType.Point_Cloud) {
            asset = this._site.getPointCloud(assetID);
        }
        else if (assetType == AssetType.Design_Data) {
            asset = this._site.getDesignData(assetID);
        }
        if (dxf) {
            await dxf.adjustHeight(asset);
        }
    }
    async resetHeightOfDXFData(id) {
        const dxf = this._site.getDXFData(id);
        await dxf?.resetHeight();
    }
    isHeightMapActive() {
        return this._site.isHeightMapActive();
    }
    setHightMapInactivate() {
        this._site.setHightMapInactivate();
    }
    setHightMapActivate() {
        this._site.setHightMapActivate();
    }
    isHightMapExist(id) {
        return this._site.isHeightMapExists(id);
    }
    isHightMapVisible(id) {
        return this._site.isHightMapVisible(id);
    }
    toggleHightMapDisplay(id) {
        this._site.toggleHightMapDisplay(this._cesiumView.viewer, id);
    }
    renderHeightMap(id, type, color) {
        this._site.renderHeightMap(id, type, color, this._cesiumView.viewer);
    }
    disposeHeightMap(id, type) {
        this._site.disposeHeightMap(id, type, this._cesiumView.viewer);
    }
    getReferencePointElevationOfTrench(id) {
        return this._trenchHandler.getReferencePointElevation(id);
    }
    getAverageHeightOfFirstAndLastPointOfTrench(id) {
        return this._trenchHandler.averageHeightOfFirstAndLastPoint(id);
    }
    //Arbitrary Line
    enableArbitraryLineCreation(callback, associatedTerrain) {
        const _associatedTerrain = this._site.getTerrain(associatedTerrain.id, associatedTerrain.terrainType);
        if (!_associatedTerrain) {
            throw new Error("Associated terrain not found");
        }
        this._arbitraryHandler.startArbitraryLineConstruction({ onFinish: callback }, _associatedTerrain);
    }
    finishArbitraryLineConstruction() {
        this._arbitraryHandler.finish();
    }
    /**
     * This Function will Disable Arbitrary Line Creation.
     */
    cancelArbitraryLineConstruction(id) {
        this._arbitraryHandler.cancel(id);
    }
    async generateLineWorkCrossSection(lineID, setting) {
        const line = this._site.getArbitraryLine(lineID);
        if (line) {
            this._lineWorkCrossSectionHandler.dispose(lineID);
            await this._lineWorkCrossSectionHandler.renderCrossSection(line, await generateTraditionalCrossSectionPoints(line, setting));
        }
    }
    async disposeLineWorksCrossSection(lineID) {
        this._lineWorkCrossSectionHandler.dispose(lineID);
    }
    async generateAlignmentCrossSection(alignmentId, setting) {
        const alignment = this._site.getCesiumRenderableAlignment(alignmentId);
        if (alignment) {
            await alignment.updateStationInterval(setting.interpolationPitch);
            this._refreshSectionSelectionIfEditing(alignmentId);
            this._lineWorkCrossSectionHandler.dispose(alignmentId);
            await this._lineWorkCrossSectionHandler.renderCrossSection(alignment, generateCenterlineCrossSectionPoints(alignment, setting));
        }
    }
    async generateCrossSectionForSima(lineID, setting) {
        const line = this._simaManager.getSIMA(lineID);
        if (line) {
            this._lineWorkCrossSectionHandler.dispose(lineID);
            await this._lineWorkCrossSectionHandler.renderCrossSection(line, generateCenterlineCrossSectionPoints(line, setting));
        }
    }
    getCrossSectionDataOfLineWorks(lineId) {
        const crossSectionData = this._crossSectionManager.getCrossSectionsForLine(lineId);
        const result = [];
        // Detach into plain objects so live Cartesian3 instances never cross the API boundary
        const toPlain = ({ x, y, z }) => ({
            x,
            y,
            z,
        });
        for (const data of crossSectionData) {
            result.push({
                referencePoint: toPlain(data.referencePoint.position),
                left: data.leftOutline.outline3D.flat().map(toPlain),
                right: data.rightOutline.outline3D.flat().map(toPlain),
            });
        }
        return result;
    }
    get2DCrossSectionDataOfLineWorks(lineId) {
        const crossSectionData = this._crossSectionManager.getCrossSectionsForLine(lineId);
        const result = [];
        for (const data of crossSectionData) {
            const { x, y, z } = data.referencePoint.position;
            result.push({
                referencePoint: { x, y, z },
                pointType: data.referencePoint.pointType,
                left: data.leftOutline.outline2D,
                right: data.rightOutline.outline2D,
                label: data.referencePoint.label,
            });
        }
        return result;
    }
    toggleArbitraryLineVisibility(lineID, show) {
        const arbitraryLine = this._site.getArbitraryLine(lineID);
        this._arbitraryHandler.toggleVisibility(arbitraryLine, show);
        this._lineWorkProfileHandler.renderProfileData(arbitraryLine);
        if (!show) {
            this._lineWorkCrossSectionHandler.dispose(lineID);
            this._lineWorkProfileHandler.dispose(lineID);
        }
    }
    getArbitraryLineJSON(lineID) {
        const arbitraryLine = this._site.getArbitraryLine(lineID);
        if (!arbitraryLine) {
            return;
        }
        const data = this._arbitraryHandler.getArbitraryLineJSON(arbitraryLine);
        return data;
    }
    fetchLineWorkJSON(lineID) {
        let data;
        const linework = this._site.getArbitraryLine(lineID);
        if (linework && linework instanceof ArbitraryLine) {
            data = this._arbitraryHandler.getArbitraryLineJSON(linework);
        }
        if (!data) {
            data = this.getSIMALineJSON(lineID);
        }
        if (!data) {
            data = this.getDXFJSON(lineID);
        }
        return data;
    }
    getCentralLineDataOfSIMA(id) {
        const sima = this._simaManager.getSIMA(id);
        if (!sima) {
            return;
        }
        let points = [];
        for (const group of sima.sima.routeByGroup) {
            const route = group.centerLine || group.mainLine || group.ipLine;
            if (route && route.points?.length) {
                for (const pt of route.points) {
                    if (pt && pt.position) {
                        points.push({
                            x: pt.position.x,
                            y: pt.position.y,
                            z: pt.position.z,
                            name: pt.point_name ||
                                (pt.point_number !== undefined ? String(pt.point_number) : ""),
                        });
                    }
                }
                if (points.length > 0)
                    break;
            }
        }
        if (points.length === 0 && sima.sima.surveyPoints?.length) {
            points = sima.sima.surveyPoints.map((sp) => ({
                x: sp.position.x,
                y: sp.position.y,
                z: sp.position.z,
                name: sp.point_name ||
                    (sp.point_number !== undefined ? String(sp.point_number) : ""),
            }));
        }
        if (points.length === 0)
            return;
        return {
            controlPoints: points.map((p, idx) => ({
                x: p.x,
                y: p.y,
                z: p.z,
                point: [p.x, p.y, p.z],
                pointType: idx === 0
                    ? PointType.BEGINNING_POINT
                    : idx === points.length - 1
                        ? PointType.END_POINT
                        : PointType.INTERMEDIATE_POINT,
                label: p.name,
            })),
            pathInfo: {
                pathPoints: points.map((p) => ({
                    position: { x: p.x, y: p.y, z: p.z },
                })),
            },
        };
    }
    getSIMALineJSON(id) {
        const data = this.getCentralLineDataOfSIMA(id);
        return data ? JSON.stringify(data) : undefined;
    }
    getCentralLineDataOfDXF(id) {
        const numId = typeof id === "string" ? Number.parseInt(id, 10) : id;
        const dxf = this._site.getDXFData(numId);
        if (!dxf)
            return;
        const dxfData = dxf.getDXFData?.();
        if (!dxfData || !dxfData.polylines || dxfData.polylines.length === 0)
            return;
        const allPoints = [];
        for (const pl of dxfData.polylines) {
            if (pl.points) {
                allPoints.push(...pl.points);
            }
        }
        if (allPoints.length === 0)
            return;
        return {
            controlPoints: allPoints.map((p, idx) => ({
                x: p.x,
                y: p.y,
                z: p.z,
                point: [p.x, p.y, p.z],
                pointType: idx === 0
                    ? PointType.BEGINNING_POINT
                    : idx === allPoints.length - 1
                        ? PointType.END_POINT
                        : PointType.INTERMEDIATE_POINT,
            })),
            pathInfo: {
                pathPoints: allPoints.map((p) => ({
                    position: { x: p.x, y: p.y, z: p.z },
                })),
            },
        };
    }
    getDXFJSON(id) {
        const data = this.getCentralLineDataOfDXF(id);
        return data ? JSON.stringify(data) : undefined;
    }
    getCentralLineDataOfAlignment(alignmentID) {
        const alignment = this._site.getAlignment(alignmentID);
        if (!alignment) {
            return;
        }
        return alignment.toCentralLineData();
    }
    checkArbitraryLineUnderCreation() {
        return this._arbitraryHandler.checkArbitraryLineUnderCreation();
    }
    /**
     * Add Arbitrary data from JSON
     * @param json JSON containing arbitrary data
     */
    async addArbitraryLinefromJSON(json) {
        const arbitraryLine = await ArbitraryLine.fromJSON(json, this._cesiumView.viewer, this._site);
        this._site.addArbitraryLine(arbitraryLine);
        this._lineWorkProfileHandler.renderProfileData(arbitraryLine);
        return arbitraryLine.id;
    }
    deleteArbitraryLine(lineID) {
        this._lineWorkCrossSectionHandler.dispose(lineID);
        this._lineWorkProfileHandler.dispose(lineID);
        this._site.deleteArbitraryLine(lineID);
    }
    async exportAlignmentToSima(alignmentId, siteName, filename = "output") {
        try {
            const alignment = this._site.getAlignment(alignmentId);
            if (!alignment) {
                throw new Error("Alignment not found");
            }
            const sima = alignmentToSIMA(alignment);
            exportToSIMAFile(sima.simaDoc.toFormat(), siteName, filename);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            throw new Error("Export failed: " + message);
        }
    }
    exportArbitraryLineToSima(lineWorksId, siteName, filename = "output") {
        this.exportLineWorksToSima(lineWorksId, siteName, filename);
    }
    exportSIMAToSima(simaId, siteName, filename = "output") {
        try {
            this._simaManager.exportSIMAToFile(simaId, filename, siteName);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            throw new Error("Export failed: " + message);
        }
    }
    exportDXFToSima(dxfId, siteName, filename = "output") {
        try {
            const jsonStr = this.getDXFJSON(dxfId);
            if (!jsonStr) {
                throw new Error("DXF data not found");
            }
            const simaAdapter = new SIMAAdapter();
            const sima = simaAdapter.prepareSIMAdata(jsonStr, siteName);
            exportToSIMAFile(sima.simaDoc.toFormat(), siteName, filename);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            throw new Error("Export failed: " + message);
        }
    }
    exportLineWorksToSima(lineWorksId, siteName, filename = "output") {
        try {
            const jsonObj = this.fetchLineWorkJSON(lineWorksId);
            if (!jsonObj) {
                throw new Error("Line works not found");
            }
            const simaAdapter = new SIMAAdapter();
            const sima = simaAdapter.prepareSIMAdata(jsonObj, siteName);
            exportToSIMAFile(sima.simaDoc.toFormat(), siteName, filename);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            throw new Error("Export failed: " + message);
        }
    }
    exportDebugLogs() {
        return FunctionLogger.getLogs();
    }
    set2DView(topLeft, topRight, bottomRight, bottomLeft) {
        this._cesiumView.viewerCamera.set2DView(topLeft, topRight, bottomRight, bottomLeft);
    }
    set3DView() {
        this._cesiumView.viewerCamera.set3DView();
    }
    set2DViewFromCurrentPosition() {
        this._cesiumView.viewerCamera.set2DViewFromCurrentPosition();
    }
    async recalculateCenterLinePositions(roadId) {
        const road = this._site.getRoad(roadId);
        if (!road)
            return [];
        return await road.recalculateCenterLinePositionsCore();
    }
    // Solid Mesh methods
    /**
     * Starts solid mesh construction
     * @param callback - Callback for construction process
     * @param shapeType - Shape type to create
     * @param shapeFactory - Optional factory to compute initial shape from picked position(s)
     */
    startSolidMeshConstruction(callback, shapeType, shapeFactory) {
        this._solidMeshHandler.startSolidMeshConstruction(callback, shapeType, shapeFactory);
    }
    /**
     * Starts multi-point solid mesh construction
     * @param callback - Callback for construction process
     * @param shapeType - Shape type to create
     * @param maxPoints - Number of points to collect
     */
    startMultiPointSolidMeshConstruction(callback, shapeType, maxPoints, shapeFactory) {
        this._solidMeshHandler.startMultiPointSolidMeshConstruction(callback, shapeType, maxPoints, shapeFactory);
    }
    /**
     * Finishes solid mesh construction
     */
    finishSolidMeshConstruction() {
        this._solidMeshHandler.finishSolidMeshConstruction();
    }
    /**
     * Cancels solid mesh construction
     */
    cancelSolidMeshConstruction() {
        this._solidMeshHandler.cancelSolidMeshConstruction();
    }
    /**
     * Undo last picked point for solid mesh multi-point construction.
     * Called by UI context menu when user selects Undo.
     */
    undoLastPickedSolidMeshPoint() {
        this._solidMeshHandler.undoLastPickedPoint();
    }
    /**
     * Return the number of points currently picked for solid mesh construction.
     */
    getSolidMeshPickedPointCount() {
        return this._solidMeshHandler.getPickedPointCount();
    }
    /**
     * Checks if solid mesh is under construction
     * @returns True if under construction
     */
    checkSolidMeshUnderConstruction() {
        return this._solidMeshHandler.checkSolidMeshUnderConstruction();
    }
    /**
     * Sets shape type during construction
     * @param shapeType - Shape type to set
     */
    setSolidMeshConstructionShapeType(shapeType) {
        this._solidMeshHandler.setSolidMeshConstructionShapeType(shapeType);
    }
    /**
     * Deletes a solid mesh
     * @param solidMeshId - Solid mesh ID
     */
    deleteSolidMesh(solidMeshId) {
        this._solidMeshHandler.deleteSolidMesh(solidMeshId);
    }
    /**
     * Sets solid mesh visibility
     * @param solidMeshId - Solid mesh ID
     * @param visible - True to show
     */
    setSolidMeshVisibility(solidMeshId, visible) {
        this._solidMeshHandler.setSolidMeshVisibility(solidMeshId, visible);
    }
    /**
     * Fits camera to solid mesh
     * @param solidMeshId - Solid mesh ID
     */
    fitToSolidMesh(solidMeshId) {
        this._solidMeshHandler.fitToSolidMesh(solidMeshId);
    }
    /**
     * Starts solid mesh editing
     * @param solidMeshId - Solid mesh ID
     * @param callback - Callback for edit process
     */
    async startSolidMeshEdit(solidMeshId, callback) {
        const solidMesh = this._site.getSolidMesh(solidMeshId);
        await this._solidMeshHandler.startSolidMeshEdit(solidMesh, callback);
    }
    /**
     * Finishes solid mesh editing
     */
    async finishSolidMeshEdit() {
        await this._solidMeshHandler.finishSolidMeshEdit();
    }
    /**
     * Cancels solid mesh editing
     */
    cancelSolidMeshEdit() {
        this._solidMeshHandler.cancelSolidMeshEdit();
    }
    /**
     * Checks if solid mesh is under edit
     * @returns True if under edit
     */
    isSolidMeshUnderEdit() {
        return this._solidMeshHandler.isSolidMeshUnderEdit();
    }
    /**
     * Rotates the solid mesh currently under edit
     * @param angles - Rotation angles [roll(X), pitch(Y), heading(Z)] in degrees
     */
    rotateSolidMesh(angles) {
        this._solidMeshHandler.rotateSolidMesh(angles);
    }
    /**
     * Rotates solid mesh relative to its baseline (creation/load-time) orientation.
     * @param solidMeshId - Solid mesh ID
     * @param delta - Delta rotation [roll, pitch, heading] in degrees
     */
    rotateSolidMeshFromBaseline(solidMeshId, delta) {
        this._solidMeshHandler.rotateSolidMeshFromBaseline(solidMeshId, delta);
    }
    /**
     * Scales the solid mesh currently under edit
     * @param scale - Scale values [x, y, z]
     */
    scaleSolidMesh(scale) {
        this._solidMeshHandler.scaleSolidMesh(scale);
    }
    /**
     * Sets solid mesh color
     * @param solidMeshId - Solid mesh ID
     * @param color - Color [r, g, b] (0-1)
     */
    setSolidMeshColor(solidMeshId, color) {
        this._solidMeshHandler.setSolidMeshColor(solidMeshId, color);
    }
    /**
     * Sets solid mesh transparency
     * @param solidMeshId - Solid mesh ID
     * @param transparency - Transparency value (0-1)
     */
    setSolidMeshTransparency(solidMeshId, transparency) {
        this._solidMeshHandler.setSolidMeshTransparency(solidMeshId, transparency);
    }
    /**
     * Sets solid mesh anchor type.
     * @param solidMeshId - Solid mesh ID
     * @param anchorType - Anchor type (including edge-midpoint for simple structures)
     */
    async setSolidMeshAnchorType(solidMeshId, anchorType, options) {
        await this._solidMeshHandler.setAnchorType(solidMeshId, anchorType, options);
    }
    /**
     * Sets solid mesh position in local coordinates.
     * @param solidMeshId - Solid mesh ID
     * @param position - Local position (anchor based on anchorType)
     */
    async setLocalPosition(solidMeshId, position) {
        await this._solidMeshHandler.setLocalPosition(solidMeshId, position);
    }
    /**
     * Gets solid mesh SavedData (shapeConfig + props)
     * @param solidMeshId - Solid mesh ID
     * @returns TSolidMeshSavedData
     */
    getSolidMeshJSON(solidMeshId) {
        return this._solidMeshHandler.getSolidMeshJSON(solidMeshId);
    }
    /**
     * Gets solid mesh rotation in degrees
     * @param solidMeshId - Solid mesh ID
     * @param options.reference - Reference angles to find closest values to (optional)
     * @param options.relativeToBaseline - If true, returns delta from baseline
     * @returns Rotation angles for each axis in degrees
     */
    getSolidMeshRotationDegrees(solidMeshId, options) {
        return this._solidMeshHandler.getRotationDegrees(solidMeshId, options);
    }
    /**
     * Resets solid mesh rotation to baseline (creation/load-time orientation).
     * @param solidMeshId - Solid mesh ID
     */
    resetSolidMeshRotationToBaseline(solidMeshId) {
        this._solidMeshHandler.resetRotationToBaseline(solidMeshId);
    }
    /**
     * Gets solid mesh anchor point in local coordinates
     * @param solidMeshId - Solid mesh ID
     * @returns Local anchor position
     */
    async getSolidMeshAnchorLocal(solidMeshId) {
        return await this._solidMeshHandler.getAnchorLocal(solidMeshId);
    }
    /**
     * Loads solid mesh from validated SavedData
     * @param data - TSolidMeshSavedData (validated by Client)
     * @returns { id: string }
     */
    async loadSolidMeshJSON(data) {
        return await this._solidMeshHandler.loadSolidMeshJSON(data);
    }
    /**
     * Applies preview-only transform in-place (no primitive dispose+rebuild).
     * Does NOT require edit mode.
     */
    async applyPreviewParams(solidMeshId, options) {
        await this._solidMeshHandler.applyPreviewParams(solidMeshId, options);
    }
    /**
     * Atomically updates a retaining wall from its two top-edge basis points.
     * Deterministic: same input always yields the same state.
     * @returns The geometry applied to the mesh
     */
    async updateRetainingWallFromTwoPoints(solidMeshId, params) {
        return await this._solidMeshHandler.updateRetainingWallFromTwoPoints(solidMeshId, params);
    }
    /**
     * Atomically updates a pipe from its two axis endpoints.
     * Deterministic: same input always yields the same state.
     * @returns The geometry applied to the mesh
     */
    async updatePipeFromTwoPoints(solidMeshId, params) {
        return await this._solidMeshHandler.updatePipeFromTwoPoints(solidMeshId, params);
    }
    /**
     * Reconstructs the two basis points of a two-point structure from the
     * authoritative quaternion, plus the baseline-relative bearing (degrees).
     * @param axis - Object-local extension axis: "z" for pipe, "x" for retaining-wall
     */
    async getSolidMeshLocalEndpoints(solidMeshId, axis) {
        return await this._solidMeshHandler.getSolidMeshLocalEndpoints(solidMeshId, axis);
    }
    /**
     * Gets solid mesh geometry data
     * @param solidMeshId - Solid mesh ID
     * @returns Vertices and indices arrays
     */
    getSolidMeshGeometry(solidMeshId) {
        return this._solidMeshHandler.getSolidMeshGeometry(solidMeshId);
    }
    // --- Spreading / Compaction ---
    /**
     * Start face selection mode on the given geometry.
     */
    async startFaceSelection(localGeometry, callback) {
        const wgs84Geometry = await this._site.convertLocalGeometryToWgs84(localGeometry);
        this._faceSelectionLocalGeometry = localGeometry;
        this._horizontalBasePlane?.dispose();
        this._horizontalBasePlane = null;
        // 選択が変わるたびに水平基準面を追従させてから client へ通知する
        this._faceSelectionHandler.start(wgs84Geometry, {
            ...callback,
            onProgress: (...args) => {
                void this.refreshHorizontalBasePlane();
                callback.onProgress?.(...args);
            },
        });
    }
    /**
     * Stop face selection mode.
     */
    stopFaceSelection() {
        this._faceSelectionHandler.stop();
        this._faceSelectionLocalGeometry = null;
        this._horizontalBasePlane?.dispose();
        this._horizontalBasePlane = null;
    }
    /**
     * How the selection is turned into a base geometry (see `FaceSelectionBaseMode`).
     * Persists across start/stop; `horizontal` also draws the plane while selecting
     * and selects only the clicked faces (no expansion).
     */
    setFaceSelectionBaseMode(mode) {
        this._faceSelectionBaseMode = mode;
        // horizontal は高さを拾うだけなので、クリックした面だけを選択し隣接面へ広げない
        this._faceSelectionHandler.setExpansionEnabled(mode === "faces");
        void this.refreshHorizontalBasePlane();
    }
    getFaceSelectionBaseMode() {
        return this._faceSelectionBaseMode;
    }
    /** 現在の選択から水平基準面 (Local) を作る。選択・対象が無ければ null */
    async buildHorizontalBaseLocalGeometry() {
        const wgs84Faces = this._faceSelectionHandler.getSelectedGeometry();
        if (!wgs84Faces || !this._faceSelectionLocalGeometry)
            return null;
        const localFaces = await this._site.convertWgs84GeometryToLocal(wgs84Faces);
        return buildHorizontalPlaneGeometry(localFaces, this._faceSelectionLocalGeometry);
    }
    async refreshHorizontalBasePlane() {
        const plane = this._faceSelectionBaseMode === "horizontal" &&
            this._faceSelectionHandler.isActive
            ? await this.buildHorizontalBaseLocalGeometry()
            : null;
        if (!plane) {
            this._horizontalBasePlane?.dispose();
            this._horizontalBasePlane = null;
            return;
        }
        const wgs84Plane = await this._site.convertLocalGeometryToWgs84(plane);
        this._horizontalBasePlane ??= new HorizontalBasePlane(this._cesiumView.viewer);
        this._horizontalBasePlane.set(wgs84Plane);
    }
    /**
     * Check if face selection mode is active.
     */
    isFaceSelectionActive() {
        return this._faceSelectionHandler.isActive;
    }
    /**
     * Set angle tolerance for face selection BFS expansion.
     */
    setFaceSelectionAngleTolerance(degrees) {
        this._faceSelectionHandler.setAngleTolerance(degrees);
    }
    /**
     * Selected faces as a Local-space base geometry. In `horizontal` mode this is
     * the horizontal plane derived from the selection instead of the faces.
     */
    async getSelectedFaceLocalGeometry() {
        if (this._faceSelectionBaseMode === "horizontal") {
            return this.buildHorizontalBaseLocalGeometry();
        }
        const wgs84Geometry = this._faceSelectionHandler.getSelectedGeometry();
        if (!wgs84Geometry)
            return null;
        return this._site.convertWgs84GeometryToLocal(wgs84Geometry);
    }
    /**
     * Extend a Local-space geometry's open boundary vertically down to the terrain.
     * Heights come from the terrain tileset's height map (nearest cell).
     * @throws when the terrain is not loaded
     */
    extendGeometryToTerrain(geometry, terrainId, terrainType, interpolationPitch) {
        const terrain = this._site.getTerrain(terrainId, terrainType);
        if (!terrain) {
            throw new Error(`Terrain not loaded in viewer: ${terrainType}:${terrainId} (asset id)`);
        }
        const info = terrain.heightMap.getObject();
        const probe = new Cartesian3();
        const sampleHeight = (x, y) => {
            probe.x = x;
            probe.y = y;
            return HeightMapUtils.getHeight(probe, info);
        };
        // Imported design data is authored with T-junctions between its flat top and
        // its slope; the consumer needs one simple outer ring, so stitch them first.
        const prepared = stitchTJunctions(weldVertices(geometry));
        const inputIssues = findNonSimpleBoundaryVertices(prepared);
        if (inputIssues.length > 0) {
            const report = describeBoundary(prepared);
            throw new Error(`Target mesh boundary is not a simple ring even after stitching; cannot extend it to the terrain. ${JSON.stringify(report)}`);
        }
        const extended = extendOpenBoundaryToTerrain(prepared, sampleHeight, interpolationPitch);
        const extendedIssues = findNonSimpleBoundaryVertices(extended);
        if (extendedIssues.length > 0) {
            throw new Error(`Terrain extension produced a non-simple boundary at ${extendedIssues.length} vertices`);
        }
        return extended;
    }
    getSpreadingCompaction(spreadingCompactionId) {
        const spreadingCompaction = this._site.getSpreadingCompaction(spreadingCompactionId);
        if (!spreadingCompaction) {
            throw new Error(`SpreadingCompaction not found: ${spreadingCompactionId}`);
        }
        return spreadingCompaction;
    }
    async createSpreadingCompaction(spreadingCompactionId, input) {
        const existing = this._site.getSpreadingCompaction(spreadingCompactionId);
        if (existing) {
            this._site.deleteSpreadingCompaction(spreadingCompactionId);
        }
        const spreadingCompaction = new SpreadingCompaction(this._cesiumView.viewer);
        await spreadingCompaction.create(input, (geom) => this._site.convertLocalGeometryToWgs84(geom));
        this._site.addSpreadingCompaction(spreadingCompactionId, spreadingCompaction);
    }
    hasSpreadingCompaction(spreadingCompactionId) {
        return (this._site.getSpreadingCompaction(spreadingCompactionId) !== undefined);
    }
    clearSpreadingCompaction(spreadingCompactionId) {
        if (!this.hasSpreadingCompaction(spreadingCompactionId))
            return;
        this._site.deleteSpreadingCompaction(spreadingCompactionId);
    }
    setSpreadingCompactionVisible(spreadingCompactionId, visible) {
        this.getSpreadingCompaction(spreadingCompactionId).setVisible(visible);
    }
    setSpreadingCompactionSurfaceVisible(spreadingCompactionId, type, offset, visible) {
        this.getSpreadingCompaction(spreadingCompactionId).setSurfaceVisible(type, offset, visible);
    }
    setSpreadingCompactionOpacity(spreadingCompactionId, alpha) {
        this.getSpreadingCompaction(spreadingCompactionId).setOpacity(alpha);
    }
    async refreshSpreadingCompactionCrossSection() {
        await this._measurementManager.updateCrossSectionByAssetType(AssetType.Spreading_Compaction_Data);
        if (this._measurementView.currentEntityID) {
            const crossSectionData = this._measurementManager.getCrossSectionData(this._measurementView.currentEntityID);
            if (crossSectionData) {
                this._measurementView.renderData(this._measurementView.currentEntityID, crossSectionData, false);
            }
        }
    }
    getSpreadingCompactionGeometries(spreadingCompactionId) {
        return this.getSpreadingCompaction(spreadingCompactionId).getAllGeometries();
    }
    /**
     * Updates attributes on an existing solid mesh.
     * @param solidMeshId - Solid mesh ID
     * @param attrs - Partial attributes to merge
     */
    updateSolidMeshAttributes(solidMeshId, attrs) {
        this._site.findSolidMesh(solidMeshId)?.updateAttributes(attrs);
    }
}
__decorate([
    needsRequestRender()
], SCDesign.prototype, "undo", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "redo", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "fitToRoad", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "toggleRoadVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setRoadAlignmentVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setRoadIpLineVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setRoadCenterLineVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setRoadSurfaceVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setRoadEmbankmentVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setRoadFillEmbankmentVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setRoadCutEmbankmentVisibility", null);
__decorate([
    needsRequestRender()
    // public async restoreRoadAfterHiding(roadID: string): Promise<void> {
    // 	const road = this._site.getRoad(roadID);
    // 	if (!road) {
    // 		return;
    // 	}
    // 	road.setRoadVisibility(true);
    // 	const slopeBreaks = road.getSlopeBreaks().map((sb) => ({
    // 		percentage: sb.percentage,
    // 		height: sb.height,
    // 		isActive: sb.isActive,
    // 		associatedControlPointId: sb.associatedControlPointId,
    // 	}));
    // 	await road.updateSlopeBreak(slopeBreaks, true);
    // 	this._update2DViews(roadID, false, AssetType.Road_Data);
    // 	this._roadHandler.editHandler.updateSectionPlaneWith2DViews();
    // }
    /**
     * Re-generates trench geometry from current in-memory settings and shows the trench.
     * Used when re-showing a trench that was hidden during an edit session.
     */
    // @needsRequestRender()
    // public async restoreTrenchAfterHiding(trenchID: string): Promise<void> {
    // 	const trench = this._site.getTrench(trenchID);
    // 	if (!trench) {
    // 		return;
    // 	}
    // 	trench.setVisibility(true);
    // 	await trench.updatePathSettings(
    // 		trench.settings,
    // 		trench.associatedRubbingDestination,
    // 	);
    // 	this._update2DViews(trenchID, false, AssetType.Trench_Data);
    // }
    /**
     * Updates the slope rendered in Altitude view, slope type can be ratio or percent
     * @param slopeTextRenderType
     */
], SCDesign.prototype, "updateLongitudeRenderingType", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateTilesetTransparency", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateTilesetColor", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateTilesetVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateDrawing2dImageVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "zoomToTileset", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "zoomToDrawing2dImage", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateDrawing2dImageTransparency", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "removeDrawing2dImage", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setElevationOfDrawing2dImage", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "addTileset", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "addDrawing2dImage", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "removeTileset", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "addPin", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "removePin", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updatePin", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "focusPin", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "makeTerrainTransparent", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "toggleGlobeVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "clear", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "focusSiteLocation", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "zoomInSite", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "zoomOutSite", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "flyToLongLatHeight", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "showGlobe", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateSlopeBreaksFromLongitudinalView", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "createAIRoadPreview", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "enableDragForAIRoadPreview", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateAIRoadPreviewPointAtIndex", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setAIRoadPreviewCallback", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "removeAIRoadPreview", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "loadRoadInfo", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "loadTrenchJSON", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "createRoadFromPoints", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "createRoadFromPointsWithAI", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "createTrenchFromPoints", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "enablePathCreation", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "enablePathCreationWithClickCount", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "addControlPointOnRoad", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "addControlPointOnCenterLineOfRoad", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "enableRoadEdit", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateRoadHorizontalAlignmentType", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateAlignmentStationInterval", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateTrenchHorizontalAlignmentType", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateCtrlPointCordiOnRoad", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateRoadSettings", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateRoadMaterialSettings", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "finishRoadConstruction", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "cancelRoadCreation", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "deleteRoad", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "removeControlPoint", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "removeSelectedSlopeBreak", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "recalculateRoadGeometry", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "fitToFlatGround", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "fitToTrench", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "enableFlatGroundCreation", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "removeFlatground", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateCtrlPointCordiOnFlatGround", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "enableFlatGroundEdit", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "removePointFromFlatGround", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "addFlatGroundControlPointOnOutline", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setFlatGroundPointToVerticalDrag", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setFlatGroundPointToHorizontalDrag", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateFlatGroundSettings", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateFlatGroundMaterialSettings", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "restoreFlatGroundAfterHiding", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "toggleFlatGroundVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setFlatGroundToUniformVerticalDrag", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "loadFlatGroundArea", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "disableFlatGroundCreationHandler", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setFlatGroundPointsUniformHeight", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "cancelFlatGroundCreation", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "enableMeasurementCreation", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "cancelMeasurement", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "loadMeasurement", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "showMeasurement", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateMeasurement", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "hideMeasurement", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "deleteMeasurement", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "zoomToMeasurement", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateTerrainAssociatedWithObject", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateRoadAndFlatLabelColors", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "enableTrenchCreation", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "checkTrenchUnderCreation", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateTrenchSettings", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateTrenchMaterialSettings", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "finishTrenchCreation", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "cancelTrenchCreation", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "deleteTrench", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setTrenchVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setTrenchCenterLineVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setTrenchIpLineVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setTrenchAlignmentVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setTrenchSurfaceVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setTrenchEmbankmentVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "enableTrenchEdit", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setSIMAVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "zoomToSIMA", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "zoomToArbitrary", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "removeSIMA", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setDXFVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "updateDXFColor", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "zoomToDXF", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "removeDXF", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "enableArbitraryLineCreation", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "finishArbitraryLineConstruction", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "cancelArbitraryLineConstruction", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "generateLineWorkCrossSection", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "disposeLineWorksCrossSection", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "generateAlignmentCrossSection", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "generateCrossSectionForSima", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "toggleArbitraryLineVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "addArbitraryLinefromJSON", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "deleteArbitraryLine", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "deleteSolidMesh", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setSolidMeshVisibility", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "stopFaceSelection", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "refreshHorizontalBasePlane", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "createSpreadingCompaction", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "clearSpreadingCompaction", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setSpreadingCompactionVisible", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setSpreadingCompactionSurfaceVisible", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "setSpreadingCompactionOpacity", null);
__decorate([
    needsRequestRender()
], SCDesign.prototype, "refreshSpreadingCompactionCrossSection", null);
//# sourceMappingURL=sc-design.js.map